import {
  AlertCircle,
  Building,
  Calendar,
  Calendar as CalendarIcon,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Clock as ClockIcon,
  CreditCard,
  DollarSign,
  Edit,
  FileText,
  Image as ImageIcon,
  Mail,
  MapPin,
  Phone,
  Plus,
  Save,
  Settings,
  Trash2,
  Users,
  X
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "../../utils/axiosHandler";

// Import the image uploader component
import ImageUploader from "./page-management/ImageUploader";

const BookingManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [services, setServices] = useState([]);
  const [activeTab, setActiveTab] = useState("bookings");
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showBookingDetails, setShowBookingDetails] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [showEditBookingForm, setShowEditBookingForm] = useState(false);

  // Time-off state
  const [timeOffs, setTimeOffs] = useState([]);
  const [showTimeOffForm, setShowTimeOffForm] = useState(false);
  const [editingTimeOff, setEditingTimeOff] = useState(null);
  const [timeOffForm, setTimeOffForm] = useState({
    serviceId: '',
    date: '',
    isFullDay: true,
    usePredefinedSlots: false,
    fullDayReason: '',
    timeSlots: []
  });

  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleBooking, setRescheduleBooking] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduleTime, setRescheduleTime] = useState("");
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [loadingTimeSlots, setLoadingTimeSlots] = useState(false);

  // Day items modal state
  const [showDayItemsModal, setShowDayItemsModal] = useState(false);
  const [dayItemsModalDate, setDayItemsModalDate] = useState(null);

  // Archive filter state
  const [showArchivedBookings, setShowArchivedBookings] = useState(false);

  // ICBC Locations state
  const [icbcLocations, setIcbcLocations] = useState([]);

  const [serviceForm, setServiceForm] = useState({
    name: "",
    slug: "",
    description: "",
    category: "driving_class",
    heroImage: "", // Add hero image field
    formFields: [],
    availableDays: [],
    timeSlots: [
      {
        startTime: "09:00",
        endTime: "17:00",
        duration: 60,
        capacity: 1,
        buffer: 15,
        isDefault: false,
      },
    ],
    pricing: {
      basePrice: 0,
      pricingType: "fixed",
      pricePerMinute: 0,
      optionPricing: [],
    },
    advanceBookingDays: 30,
    minNoticeHours: 2,
    maxBookingsPerCustomer: 0,
    requiresPayment: false,
    paymentType: "full_upfront",
    depositAmount: 0,
    hasGST: false,
    hasPST: false,
    isActive: true,
  });

  const [newField, setNewField] = useState({
    fieldId: "",
    label: "",
    type: "text",
    required: false,
    placeholder: "",
    options: [""],
    optionPrices: [{}], // Pricing for each option - now supports time slot specific pricing { duration: price }
    order: 0,
  });

  const [expandedField, setExpandedField] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editFieldData, setEditFieldData] = useState({});

  const fieldTypes = [
    { value: "text", label: "Text Input" },
    { value: "textarea", label: "Text Area" },
    { value: "select", label: "Dropdown" },
    { value: "radio", label: "Radio Buttons" },
    { value: "checkbox", label: "Checkboxes" },
    { value: "date", label: "Date" },
    { value: "time", label: "Time" },
    { value: "number", label: "Number" },
    { value: "email", label: "Email" },
    { value: "phone", label: "Phone" },
  ];

  const categories = [
    { value: "driving_class", label: "Driving Class" },
    { value: "restaurant", label: "Restaurant" },
    { value: "appointment", label: "Appointment" },
    { value: "consultation", label: "Consultation" },
    { value: "other", label: "Other" },
  ];

  const daysOfWeek = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  useEffect(() => {
    const checkIsMobile = () => setIsMobile(window.innerWidth < 768);
    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  useEffect(() => {
    fetchServices();
    fetchIcbcLocations();
    if (activeTab === "bookings" || activeTab === "calendar") {
      fetchBookings();
    }
    if (activeTab === "timeoff" || activeTab === "calendar") {
      fetchTimeOffs();
    }
  }, [activeTab]);

  // Handle deep-linking to a specific booking from URL parameter (e.g., from SMS notification)
  useEffect(() => {
    const bookingId = searchParams.get('booking');
    if (bookingId && bookings.length > 0) {
      const booking = bookings.find(b => b._id === bookingId);
      if (booking) {
        setSelectedBooking(booking);
        setShowBookingDetails(true);
        setActiveTab("bookings"); // Switch to bookings tab
        // Clear the URL parameter after opening the modal
        searchParams.delete('booking');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [bookings, searchParams, setSearchParams]);

  // Set default service when services are loaded
  useEffect(() => {
    if (services.length > 0 && !timeOffForm.serviceId && showTimeOffForm) {
      setTimeOffForm(prev => ({ ...prev, serviceId: services[0]._id }));
    }
  }, [services, timeOffForm.serviceId, showTimeOffForm]);

  // Debug useEffect to track timeOffForm changes
  useEffect(() => {
    if (editingTimeOff && timeOffForm.timeSlots.length > 0) {
      console.log('timeOffForm state updated:', timeOffForm);
      console.log('First slot isStudentBooking after state update:', timeOffForm.timeSlots[0]?.isStudentBooking);
      console.log('First slot studentInfo after state update:', timeOffForm.timeSlots[0]?.studentInfo);
    }
  }, [timeOffForm, editingTimeOff]);

  // Update selected booking when bookings list changes (to reflect time-off updates)
  useEffect(() => {
    if (selectedBooking && bookings.length > 0) {
      const updatedBooking = bookings.find(booking => booking._id === selectedBooking._id);
      if (updatedBooking) {
        // Check if customer info has changed
        const customerInfoChanged = JSON.stringify(updatedBooking.customerInfo) !== JSON.stringify(selectedBooking.customerInfo);
        const formResponsesChanged = JSON.stringify(updatedBooking.formResponses) !== JSON.stringify(selectedBooking.formResponses);

        if (customerInfoChanged || formResponsesChanged) {
          console.log('Updating selected booking with fresh data:', {
            oldCustomerInfo: selectedBooking.customerInfo,
            newCustomerInfo: updatedBooking.customerInfo,
            customerInfoChanged,
            formResponsesChanged
          });
          setSelectedBooking(updatedBooking);
        }
      }
    }
  }, [bookings, selectedBooking]);

  const fetchServices = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get("/api/admin/booking-services", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setServices(response.data);
    } catch (error) {
      console.error("Error fetching services:", error);
    }
  };

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get("/api/admin/bookings", {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 1000 }, // Fetch all bookings, not just default 20
      });
      setBookings(response.data.bookings);
    } catch (error) {
      console.error("Error fetching bookings:", error);
    }
  };

  const fetchTimeOffs = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get("/api/admin/time-off", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTimeOffs(response.data.timeOffs);
    } catch (error) {
      console.error("Error fetching time-offs:", error);
    }
  };

  const fetchIcbcLocations = async () => {
    try {
      const response = await axios.get("/api/icbc/locations");
      if (response.data.success) {
        setIcbcLocations(response.data.locations);
      }
    } catch (error) {
      console.error("Error fetching ICBC locations:", error);
      setIcbcLocations([]);
    }
  };

  const handleCreateTimeOff = async () => {
    try {
      const token = localStorage.getItem("token");
      let deletedCount = 0; // Track deleted time-offs for success message

      // Validate student booking information if any slot is a student booking
      for (const slot of timeOffForm.timeSlots) {
        if (slot.isStudentBooking) {
          if (!slot.studentInfo.firstName || slot.studentInfo.firstName.trim() === '') {
            alert('Please enter the student\'s first name for all student bookings.');
            return;
          }
          if (!slot.studentInfo.email || slot.studentInfo.email.trim() === '') {
            alert('Please enter the student\'s email for all student bookings.');
            return;
          }
          if (!slot.studentInfo.phone || slot.studentInfo.phone.trim() === '') {
            alert('Please enter the student\'s phone number for all student bookings.');
            return;
          }
        }
      }

      if (editingTimeOff) {
        console.log('Editing mode: processing', timeOffForm.timeSlots.length, 'time slots');

        // First, we need to find which time-offs should be deleted (removed from the form)
        // Get all original time-off IDs that were loaded into the form
        const originalTimeOffIds = [editingTimeOff._id];

        // Add any related time-off IDs that were originally loaded
        if (editingTimeOff.originalRelatedTimeOffIds) {
          originalTimeOffIds.push(...editingTimeOff.originalRelatedTimeOffIds);
        }

        console.log('Original time-off IDs that were loaded:', originalTimeOffIds);

        // Find which time-offs are still in the form (not deleted)
        const remainingTimeOffIds = [];
        remainingTimeOffIds.push(editingTimeOff._id); // Main time-off always remains (gets updated)

        timeOffForm.timeSlots.forEach(slot => {
          if (slot.originalTimeOffId) {
            remainingTimeOffIds.push(slot.originalTimeOffId);
          }
        });

        console.log('Remaining time-off IDs in form:', remainingTimeOffIds);

        // Find time-offs that need to be deleted (were original but not remaining)
        const timeOffsToDelete = originalTimeOffIds.filter(id =>
          id !== editingTimeOff._id && !remainingTimeOffIds.includes(id)
        );

        console.log('Time-offs to delete:', timeOffsToDelete);

        // Delete removed time-offs first
        for (const timeOffIdToDelete of timeOffsToDelete) {
          console.log('Deleting removed time-off:', timeOffIdToDelete);
          try {
            await axios.delete(`/api/admin/time-off/${timeOffIdToDelete}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            console.log('Successfully deleted time-off:', timeOffIdToDelete);
            deletedCount++; // Increment successful deletion count
          } catch (error) {
            console.error('Error deleting time-off:', timeOffIdToDelete, error);
          }
        }

        // Now handle each remaining time slot - update existing or create new ones
        for (let i = 0; i < timeOffForm.timeSlots.length; i++) {
          const slot = timeOffForm.timeSlots[i];
          console.log(`Processing slot ${i + 1}:`, slot);

          // Check if this slot has an original time-off ID (from related time-offs)
          if (slot.originalTimeOffId) {
            // Update existing related time-off
            console.log('Updating existing related time-off:', slot.originalTimeOffId);

            const updateData = {
              serviceId: timeOffForm.serviceId,
              date: timeOffForm.date,
              isFullDay: timeOffForm.isFullDay,
              startTime: slot.startTime,
              endTime: slot.endTime,
              reason: slot.reason,
              isStudentBooking: slot.isStudentBooking,
              studentInfo: slot.isStudentBooking ? slot.studentInfo : undefined,
              associatedBooking: slot.associatedBooking // Include booking association for related time-offs
            };

            await axios.put(
              `/api/admin/time-off/${slot.originalTimeOffId}`,
              updateData,
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );

            // Check if this is a student booking
            if (slot.isStudentBooking && slot.studentInfo &&
              (slot.studentInfo.firstName || slot.studentInfo.lastName || slot.studentInfo.email)) {

              // If there's already an associated booking, update it
              if (slot.associatedBooking) {
                console.log('Updating existing associated booking for related time-off:', slot.associatedBooking);

                try {
                  // Calculate duration in minutes
                  const startMinutes = parseInt(slot.startTime.split(':')[0]) * 60 + parseInt(slot.startTime.split(':')[1]);
                  const endMinutes = parseInt(slot.endTime.split(':')[0]) * 60 + parseInt(slot.endTime.split(':')[1]);
                  const duration = endMinutes - startMinutes;

                  const updateBookingData = {
                    customerInfo: {
                      firstName: slot.studentInfo.firstName || '',
                      lastName: slot.studentInfo.lastName || '',
                      email: slot.studentInfo.email || '',
                      phone: slot.studentInfo.phone || '',
                      company: slot.studentInfo.company || '',
                      location: slot.studentInfo.icbcLocation || ''
                    },
                    notes: slot.studentInfo.notes || slot.reason || 'Student booking'
                  };

                  await axios.put(
                    `/api/admin/bookings/${slot.associatedBooking}`,
                    updateBookingData,
                    {
                      headers: { Authorization: `Bearer ${token}` },
                    }
                  );
                  console.log('Successfully updated associated booking for related time-off');
                } catch (bookingError) {
                  console.error('Error updating associated booking:', bookingError);
                  alert(`Warning: Could not update the associated booking. Error: ${bookingError.message}`);
                }
              } else {
                // No associated booking yet, create one
                console.log('Converting related time-off to student booking - creating booking');
                const bookingId = await createStudentBooking(slot, token);

                // Link the booking to the time-off
                if (bookingId) {
                  console.log('Linking booking to converted related time-off:', { bookingId, timeOffId: slot.originalTimeOffId });
                  await axios.put(`/api/admin/time-off/${slot.originalTimeOffId}`, {
                    ...updateData,
                    associatedBooking: bookingId
                  }, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                }
              }
            }

          } else if (i === 0) {
            // Update the main time-off (first slot without originalTimeOffId)
            console.log('Updating main time-off:', editingTimeOff._id);

            const legacyData = {
              serviceId: timeOffForm.serviceId,
              date: timeOffForm.date,
              isFullDay: timeOffForm.isFullDay,
              startTime: slot.startTime,
              endTime: slot.endTime,
              reason: slot.reason,
              isStudentBooking: slot.isStudentBooking,
              studentInfo: slot.isStudentBooking ? slot.studentInfo : undefined,
              associatedBooking: editingTimeOff.associatedBooking // Include the existing booking association
            };

            console.log('Updating main time-off with data:', legacyData);

            await axios.put(
              `/api/admin/time-off/${editingTimeOff._id}`,
              legacyData,
              {
                headers: { Authorization: `Bearer ${token}` },
              }
            );

            // Check if this is a student booking
            if (slot.isStudentBooking && slot.studentInfo &&
              (slot.studentInfo.firstName || slot.studentInfo.lastName || slot.studentInfo.email)) {

              // If there's already an associated booking, update it
              if (editingTimeOff.associatedBooking) {
                console.log('Updating existing associated booking:', editingTimeOff.associatedBooking);

                try {
                  // Calculate duration in minutes
                  const startMinutes = parseInt(slot.startTime.split(':')[0]) * 60 + parseInt(slot.startTime.split(':')[1]);
                  const endMinutes = parseInt(slot.endTime.split(':')[0]) * 60 + parseInt(slot.endTime.split(':')[1]);
                  const duration = endMinutes - startMinutes;

                  const updateBookingData = {
                    customerInfo: {
                      firstName: slot.studentInfo.firstName || '',
                      lastName: slot.studentInfo.lastName || '',
                      email: slot.studentInfo.email || '',
                      phone: slot.studentInfo.phone || '',
                      company: slot.studentInfo.company || '',
                      location: slot.studentInfo.icbcLocation || ''
                    },
                    notes: slot.studentInfo.notes || slot.reason || 'Student booking'
                  };

                  await axios.put(
                    `/api/admin/bookings/${editingTimeOff.associatedBooking}`,
                    updateBookingData,
                    {
                      headers: { Authorization: `Bearer ${token}` },
                    }
                  );
                  console.log('Successfully updated associated booking');
                } catch (bookingError) {
                  console.error('Error updating associated booking:', bookingError);
                  alert(`Warning: Could not update the associated booking. Error: ${bookingError.message}`);
                }
              } else {
                // No associated booking yet, create one
                console.log('Converting regular time-off to student booking - creating booking');
                const bookingId = await createStudentBooking(slot, token);

                // Link the booking to the time-off
                if (bookingId) {
                  console.log('Linking booking to converted time-off:', { bookingId, timeOffId: editingTimeOff._id });
                  await axios.put(`/api/admin/time-off/${editingTimeOff._id}`, {
                    ...legacyData,
                    associatedBooking: bookingId
                  }, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                }
              }
            }
          } else {
            // Create new time-off for additional slots
            console.log('Creating new time-off for additional slot');

            const singleTimeOffData = {
              serviceId: timeOffForm.serviceId,
              date: timeOffForm.date,
              isFullDay: timeOffForm.isFullDay,
              startTime: slot.startTime,
              endTime: slot.endTime,
              reason: slot.reason,
              isStudentBooking: slot.isStudentBooking,
              studentInfo: slot.isStudentBooking ? slot.studentInfo : undefined
            };

            const timeOffResponse = await axios.post("/api/admin/time-off", singleTimeOffData, {
              headers: { Authorization: `Bearer ${token}` },
            });

            // Additionally, if this is a student booking with info, create a booking too
            if (slot.isStudentBooking && slot.studentInfo &&
              (slot.studentInfo.firstName || slot.studentInfo.lastName || slot.studentInfo.email)) {

              const bookingId = await createStudentBooking(slot, token);

              // Link the booking to the time-off
              if (bookingId && timeOffResponse.data.timeOff) {
                console.log('Linking booking to new time-off:', { bookingId, timeOffId: timeOffResponse.data.timeOff._id });
                await axios.put(`/api/admin/time-off/${timeOffResponse.data.timeOff._id}`, {
                  ...singleTimeOffData,
                  associatedBooking: bookingId
                }, {
                  headers: { Authorization: `Bearer ${token}` },
                });
              }
            }
          }
        }
      } else {
        // For creating, handle each slot or create full day time-off
        if (timeOffForm.isFullDay) {
          // Create a single full-day time-off entry
          const fullDayTimeOffData = {
            serviceId: timeOffForm.serviceId,
            date: timeOffForm.date,
            isFullDay: true,
            reason: timeOffForm.fullDayReason || '',
            isStudentBooking: false,
            studentInfo: undefined
          };

          await axios.post("/api/admin/time-off", fullDayTimeOffData, {
            headers: { Authorization: `Bearer ${token}` },
          });
        } else {
          // Handle individual time slots
          for (const slot of timeOffForm.timeSlots) {
            console.log('Creating time-off for slot:', slot); // Debug log
            console.log('Slot student info:', slot.studentInfo); // Debug student info specifically

            // Always create a time-off entry to show in the calendar
            const singleTimeOffData = {
              serviceId: timeOffForm.serviceId,
              date: timeOffForm.date,
              isFullDay: timeOffForm.isFullDay,
              startTime: slot.startTime,
              endTime: slot.endTime,
              reason: slot.reason,
              isStudentBooking: slot.isStudentBooking,
              studentInfo: slot.isStudentBooking ? slot.studentInfo : undefined
            };

            console.log('Time-off data being sent to server:', singleTimeOffData); // Debug what we're sending

            const timeOffResponse = await axios.post("/api/admin/time-off", singleTimeOffData, {
              headers: { Authorization: `Bearer ${token}` },
            });

            // Additionally, if this is a student booking with info, create a booking too
            if (slot.isStudentBooking && slot.studentInfo &&
              (slot.studentInfo.firstName || slot.studentInfo.lastName || slot.studentInfo.email)) {

              console.log('Creating student booking for time-off:', timeOffResponse.data.timeOff._id);
              const bookingId = await createStudentBooking(slot, token);

              // Link the booking to the time-off
              if (bookingId && timeOffResponse.data.timeOff) {
                console.log('Linking booking to time-off:', { bookingId, timeOffId: timeOffResponse.data.timeOff._id });
                await axios.put(`/api/admin/time-off/${timeOffResponse.data.timeOff._id}`, {
                  ...singleTimeOffData,
                  associatedBooking: bookingId
                }, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                console.log('Successfully linked booking to time-off');
              }
            }
          }
        }
      }

      // Refresh data with proper timing
      console.log('Refreshing time-offs and bookings after update...');
      await fetchTimeOffs();
      await fetchBookings(); // Also refresh bookings since we might have created/updated bookings

      // Force refresh of selected booking if it exists
      if (selectedBooking) {
        console.log('Refreshing selected booking after time-off update');
        // Wait a bit to ensure the booking update has been processed
        await new Promise(resolve => setTimeout(resolve, 1500));

        const updatedBookings = await axios.get("/api/admin/bookings", {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log('Fresh bookings data received:', updatedBookings.data.bookings.length, 'bookings');

        const updatedSelectedBooking = updatedBookings.data.bookings.find(
          booking => booking._id === selectedBooking._id
        );
        if (updatedSelectedBooking) {
          console.log('Found updated selected booking:', {
            id: updatedSelectedBooking._id,
            oldCustomerInfo: selectedBooking.customerInfo,
            newCustomerInfo: updatedSelectedBooking.customerInfo,
            oldLastName: selectedBooking.customerInfo?.lastName,
            newLastName: updatedSelectedBooking.customerInfo?.lastName
          });
          setSelectedBooking(updatedSelectedBooking);
          console.log('selectedBooking state updated to:', updatedSelectedBooking.customerInfo);
        } else {
          console.log('Selected booking not found in updated list - it may have been deleted');
          setSelectedBooking(null);
          setShowBookingDetails(false);
        }
      }

      // Also force a complete refresh of the bookings list
      console.log('Forcing complete bookings refresh...');
      setTimeout(async () => {
        try {
          await fetchBookings();
          console.log('Delayed bookings refresh completed');
        } catch (error) {
          console.error('Error in delayed bookings refresh:', error);
        }
      }, 1000);

      resetTimeOffForm();

      // Show success message
      const studentBookings = timeOffForm.isFullDay ? 0 : timeOffForm.timeSlots.filter(slot =>
        slot.isStudentBooking && (slot.studentInfo.firstName || slot.studentInfo.lastName || slot.studentInfo.email)
      ).length;

      if (editingTimeOff) {
        let message = 'Time-off updated successfully!';

        if (studentBookings > 0 && deletedCount > 0) {
          message = `Success! Updated ${studentBookings} student booking(s) and deleted ${deletedCount} removed booking(s).`;
        } else if (studentBookings > 0) {
          message = `Success! Updated time-off and ${studentBookings} associated booking(s).`;
        } else if (deletedCount > 0) {
          message = `Success! Updated time-off and deleted ${deletedCount} removed booking(s).`;
        }

        alert(message);
      } else {
        if (studentBookings > 0) {
          alert(`Success! Created ${timeOffForm.timeSlots.length} time-off period(s) and ${studentBookings} student booking(s).`);
        } else {
          alert('Time-off period(s) created successfully!');
        }
      }
    } catch (error) {
      console.error("Error saving time-off:", error);
      alert(
        "Error saving time-off: " +
        (error.response?.data?.error || error.message)
      );
    }
  };

  // Function to add single lesson to cart (similar to package selection)
  const addSingleLessonToCart = (slot, service) => {
    try {
      // Calculate duration in minutes
      const startTime = slot.startTime;
      const endTime = slot.endTime;
      const startMinutes = parseInt(startTime.split(':')[0]) * 60 + parseInt(startTime.split(':')[1]);
      const endMinutes = parseInt(endTime.split(':')[0]) * 60 + parseInt(endTime.split(':')[1]);
      const duration = endMinutes - startMinutes;

      const singleLessonData = {
        class: service.name,
        name: "Single Lesson",
        price: 0, // Admin bookings are typically free or handled separately
        lessons: 1,
        duration: duration,
        savings: 0,
        description: `${duration} minute lesson on ${timeOffForm.date} at ${startTime}`,
        bookingPageUrl: window.location.pathname,
        bookingType: "single",
        date: timeOffForm.date,
        startTime: startTime,
        endTime: endTime,
        studentInfo: slot.studentInfo,
        serviceId: timeOffForm.serviceId,
        timestamp: Date.now()
      };

      localStorage.setItem('selectedBookingPackage', JSON.stringify(singleLessonData));

      // Dispatch a custom event to notify the Navbar and Cart
      window.dispatchEvent(new Event('bookingPackageUpdated'));

      console.log('Single lesson added to cart:', singleLessonData);
    } catch (error) {
      console.error("Error adding single lesson to cart:", error);
    }
  };

  const createStudentBooking = async (slot, token) => {
    try {
      // Find the selected service
      const selectedService = services.find(s => s._id === timeOffForm.serviceId);
      if (!selectedService) {
        throw new Error("Service not found");
      }

      // Calculate duration in minutes
      const startTime = slot.startTime;
      const endTime = slot.endTime;
      const startMinutes = parseInt(startTime.split(':')[0]) * 60 + parseInt(startTime.split(':')[1]);
      const endMinutes = parseInt(endTime.split(':')[0]) * 60 + parseInt(endTime.split(':')[1]);
      const duration = endMinutes - startMinutes;

      // Prepare form responses for the booking
      const formResponses = [];
      if (selectedService.formFields && slot.studentInfo.formResponses) {
        selectedService.formFields.forEach(field => {
          const value = slot.studentInfo.formResponses[field.fieldId];
          if (value !== undefined && value !== '') {
            formResponses.push({
              fieldId: field.fieldId,
              label: field.label,
              value: value
            });
          }
        });
      }

      // Create the booking data
      const bookingData = {
        serviceId: timeOffForm.serviceId,
        bookingDate: timeOffForm.date,
        startTime: startTime,
        endTime: endTime,
        duration: duration,
        customerInfo: {
          firstName: slot.studentInfo.firstName || '',
          lastName: slot.studentInfo.lastName || ' ',
          email: slot.studentInfo.email || 'no-email@admin-booking.com',
          phone: slot.studentInfo.phone || '',
          company: slot.studentInfo.company || '',
          location: slot.studentInfo.icbcLocation || ''
        },
        formResponses: formResponses,
        pricing: {
          basePrice: 0,
          optionPrices: 0,
          subtotal: 0,
          gst: 0,
          pst: 0,
          totalAmount: 0
        },
        status: 'confirmed',
        paymentStatus: 'not_required',
        notes: slot.studentInfo.notes || `Admin booking via time-off: ${slot.reason || 'Student booking'}`,
        isAdminBooking: true
      };

      // Create the booking
      const bookingResponse = await axios.post("/api/admin/bookings", bookingData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log('Created booking:', bookingResponse.data);

      // Add the single lesson to cart
      addSingleLessonToCart(slot, selectedService);

      // Return the booking ID so it can be stored in the time-off
      return bookingResponse.data.booking._id;

    } catch (error) {
      console.error("Error creating student booking:", error);
      // Don't throw the error, just log it so the time-off creation can continue
      alert(`Warning: Could not create booking for student, but time-off was created. Error: ${error.message}`);
    }
  };

  const handleDeleteTimeOff = async (timeOffId) => {
    // Find the time-off to check if it's a student booking
    const timeOff = timeOffs.find(t => t._id === timeOffId);
    const isStudentBooking = timeOff?.isStudentBooking && timeOff?.associatedBooking;

    let confirmMessage = "Are you sure you want to delete this time-off period?";
    if (isStudentBooking) {
      confirmMessage = "Are you sure you want to delete this time-off period? This will also delete the associated student booking.";
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/admin/time-off/${timeOffId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      fetchTimeOffs();
      fetchBookings(); // Refresh bookings in case the associated booking was also deleted

      if (isStudentBooking) {
        alert("Time-off and associated booking deleted successfully!");
      } else {
        alert("Time-off deleted successfully!");
      }
    } catch (error) {
      console.error("Error deleting time-off:", error);
      alert("Error deleting time-off: " + (error.response?.data?.error || error.message));
    }
  };

  const handleEditTimeOff = async (timeOff) => {
    console.log('Editing time-off:', timeOff); // Debug log
    console.log('Student info from time-off:', timeOff.studentInfo); // More specific debug
    setEditingTimeOff(timeOff);

    try {
      // If this is a student booking time-off, we need to check if there are multiple associated bookings
      // that should be edited together (same date, same service, similar time slots)
      let timeSlots = [];

      if (timeOff.isStudentBooking && timeOff.associatedBooking) {
        console.log('Checking for related student bookings for comprehensive editing...');

        const token = localStorage.getItem("token");

        // Get all bookings for the same date and service to see if there are multiple student bookings
        const bookingsResponse = await axios.get("/api/admin/bookings", {
          headers: { Authorization: `Bearer ${token}` },
        });

        // Find bookings that are on the same date and service (potential related student bookings)
        const relatedBookings = bookingsResponse.data.bookings.filter(booking => {
          const bookingDate = new Date(booking.bookingDate).toDateString();
          const timeOffDate = new Date(timeOff.date).toDateString();
          const sameService = booking.service._id === (timeOff.serviceId._id || timeOff.serviceId);
          const sameDate = bookingDate === timeOffDate;

          return sameDate && sameService;
        });

        console.log(`Found ${relatedBookings.length} bookings on the same date and service`);

        // Check if any of these bookings have associated time-offs that are student bookings
        const allTimeOffs = await axios.get("/api/admin/time-off", {
          headers: { Authorization: `Bearer ${token}` },
        });

        const relatedTimeOffs = allTimeOffs.data.timeOffs.filter(to => {
          const timeOffDate = new Date(to.date).toDateString();
          const originalDate = new Date(timeOff.date).toDateString();
          const sameService = (to.serviceId._id || to.serviceId) === (timeOff.serviceId._id || timeOff.serviceId);
          const sameDate = timeOffDate === originalDate;

          return sameDate && sameService && to.isStudentBooking;
        });

        console.log(`Found ${relatedTimeOffs.length} student booking time-offs on the same date`);

        // Create time slots for each related time-off
        timeSlots = relatedTimeOffs.map((relatedTimeOff, index) => {
          const studentInfo = {
            firstName: relatedTimeOff.studentInfo?.firstName || '',
            lastName: relatedTimeOff.studentInfo?.lastName || '',
            email: relatedTimeOff.studentInfo?.email || '',
            phone: relatedTimeOff.studentInfo?.phone || '',
            company: relatedTimeOff.studentInfo?.company || '',
            icbcLocation: relatedTimeOff.studentInfo?.icbcLocation || '',
            notes: relatedTimeOff.studentInfo?.notes || '',
            formResponses: relatedTimeOff.studentInfo?.formResponses || {}
          };

          return {
            id: Date.now() + index,
            startTime: relatedTimeOff.startTime || '09:00',
            endTime: relatedTimeOff.endTime || '17:00',
            reason: relatedTimeOff.reason || '',
            isStudentBooking: true,
            studentInfo: studentInfo,
            originalTimeOffId: relatedTimeOff._id, // Keep track of original time-off ID
            associatedBooking: relatedTimeOff.associatedBooking // Keep track of associated booking ID
          };
        });

        // Store the original time-off IDs for deletion tracking
        const originalTimeOffIds = relatedTimeOffs.map(to => to._id);
        console.log('Storing original time-off IDs for deletion tracking:', originalTimeOffIds);

        // Store this in the editing state so we can track deletions
        setEditingTimeOff({
          ...timeOff,
          originalRelatedTimeOffIds: originalTimeOffIds
        });

      } else {
        // Single time-off (regular or single student booking)
        const studentInfo = {
          firstName: timeOff.studentInfo?.firstName || '',
          lastName: timeOff.studentInfo?.lastName || '',
          email: timeOff.studentInfo?.email || '',
          phone: timeOff.studentInfo?.phone || '',
          company: timeOff.studentInfo?.company || '',
          icbcLocation: timeOff.studentInfo?.icbcLocation || '',
          notes: timeOff.studentInfo?.notes || '',
          formResponses: timeOff.studentInfo?.formResponses || {}
        };

        timeSlots = [{
          id: Date.now(),
          startTime: timeOff.startTime || '09:00',
          endTime: timeOff.endTime || '17:00',
          reason: timeOff.reason || '',
          isStudentBooking: timeOff.isStudentBooking || false,
          studentInfo: studentInfo,
          associatedBooking: timeOff.associatedBooking // Keep track of associated booking ID
        }];
      }

      const formData = {
        serviceId: timeOff.serviceId._id || timeOff.serviceId,
        date: new Date(timeOff.date).toISOString().split('T')[0],
        isFullDay: timeOff.isFullDay,
        usePredefinedSlots: false,
        fullDayReason: timeOff.isFullDay ? (timeOff.reason || '') : '',
        timeSlots: timeOff.isFullDay ? [] : timeSlots
      };

      console.log('Setting form data with', timeSlots.length, 'time slots:', formData);
      setTimeOffForm(formData);

    } catch (error) {
      console.error('Error loading related bookings for editing:', error);

      // Fallback to single time-off editing
      const studentInfo = {
        firstName: timeOff.studentInfo?.firstName || '',
        lastName: timeOff.studentInfo?.lastName || '',
        email: timeOff.studentInfo?.email || '',
        phone: timeOff.studentInfo?.phone || '',
        company: timeOff.studentInfo?.company || '',
        icbcLocation: timeOff.studentInfo?.icbcLocation || '',
        notes: timeOff.studentInfo?.notes || '',
        formResponses: timeOff.studentInfo?.formResponses || {}
      };

      const formData = {
        serviceId: timeOff.serviceId._id || timeOff.serviceId,
        date: new Date(timeOff.date).toISOString().split('T')[0],
        isFullDay: timeOff.isFullDay,
        usePredefinedSlots: false,
        fullDayReason: timeOff.isFullDay ? (timeOff.reason || '') : '',
        timeSlots: timeOff.isFullDay ? [] : [
          {
            id: Date.now(),
            startTime: timeOff.startTime || '09:00',
            endTime: timeOff.endTime || '17:00',
            reason: timeOff.reason || '',
            isStudentBooking: timeOff.isStudentBooking || false,
            studentInfo: studentInfo,
            associatedBooking: timeOff.associatedBooking // Keep track of associated booking ID
          }
        ]
      };

      setTimeOffForm(formData);
    }

    // Small delay to ensure form state is updated before showing
    setTimeout(() => {
      console.log('About to show form - current timeOffForm state:', timeOffForm); // Debug current state
      setShowTimeOffForm(true);
    }, 100);
  };

  const resetTimeOffForm = () => {
    setTimeOffForm({
      serviceId: '',
      date: '',
      isFullDay: true,
      usePredefinedSlots: false,
      fullDayReason: '',
      timeSlots: []
    });
    setEditingTimeOff(null);
    setShowTimeOffForm(false);
  };

  // Helper functions for managing time-off slots
  const addTimeOffSlot = () => {
    const newSlot = {
      id: Date.now(),
      startTime: '09:00',
      endTime: '17:00',
      reason: '',
      isStudentBooking: false,
      studentInfo: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        notes: '',
        formResponses: {}
      }
    };
    setTimeOffForm({
      ...timeOffForm,
      timeSlots: [...timeOffForm.timeSlots, newSlot]
    });
  };

  const removeTimeOffSlot = (slotId) => {
    if (timeOffForm.timeSlots.length > 1) {
      setTimeOffForm({
        ...timeOffForm,
        timeSlots: timeOffForm.timeSlots.filter(slot => slot.id !== slotId)
      });
    }
  };

  const updateTimeOffSlot = (slotId, field, value) => {
    setTimeOffForm({
      ...timeOffForm,
      timeSlots: timeOffForm.timeSlots.map(slot =>
        slot.id === slotId ? { ...slot, [field]: value } : slot
      )
    });
  };

  const updateStudentInfo = (slotId, field, value) => {
    console.log('Updating student info:', { slotId, field, value }); // Debug log

    const updatedForm = {
      ...timeOffForm,
      timeSlots: timeOffForm.timeSlots.map(slot =>
        slot.id === slotId
          ? { ...slot, studentInfo: { ...slot.studentInfo, [field]: value } }
          : slot
      )
    };

    console.log('Updated timeOffForm:', updatedForm); // Debug the updated form
    console.log('Updated slot student info:', updatedForm.timeSlots.find(s => s.id === slotId)?.studentInfo); // Debug specific slot

    setTimeOffForm(updatedForm);
  };

  const updateStudentFormResponse = (slotId, fieldId, value) => {
    setTimeOffForm({
      ...timeOffForm,
      timeSlots: timeOffForm.timeSlots.map(slot =>
        slot.id === slotId
          ? {
            ...slot,
            studentInfo: {
              ...slot.studentInfo,
              formResponses: { ...slot.studentInfo.formResponses, [fieldId]: value }
            }
          }
          : slot
      )
    });
  };

  const renderStudentFormField = (field, slotId, value) => {
    switch (field.type) {
      case "text":
      case "email":
      case "phone":
        return (
          <input
            type={field.type}
            value={value || ""}
            onChange={(e) => updateStudentFormResponse(slotId, field.fieldId, e.target.value)}
            placeholder={field.placeholder}
            className="w-full border rounded px-2 py-1 text-sm"
          />
        );

      case "textarea":
        return (
          <textarea
            value={value || ""}
            onChange={(e) => updateStudentFormResponse(slotId, field.fieldId, e.target.value)}
            placeholder={field.placeholder}
            rows="2"
            className="w-full border rounded px-2 py-1 text-sm"
          />
        );

      case "select":
        return (
          <select
            value={value || ""}
            onChange={(e) => updateStudentFormResponse(slotId, field.fieldId, e.target.value)}
            className="w-full border rounded px-2 py-1 text-sm"
          >
            <option value="">Select an option...</option>
            {field.options?.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case "radio":
        return (
          <div className="space-y-1">
            {field.options?.map((option, index) => (
              <label key={index} className="flex items-center space-x-1">
                <input
                  type="radio"
                  name={`${slotId}-${field.fieldId}`}
                  value={option}
                  checked={value === option}
                  onChange={(e) => updateStudentFormResponse(slotId, field.fieldId, e.target.value)}
                  className="text-blue-600"
                />
                <span className="text-sm">{option}</span>
              </label>
            ))}
          </div>
        );

      case "checkbox":
        return (
          <div className="space-y-1">
            {field.options?.map((option, index) => (
              <label key={index} className="flex items-center space-x-1">
                <input
                  type="checkbox"
                  value={option}
                  checked={Array.isArray(value) ? value.includes(option) : false}
                  onChange={(e) => {
                    const currentValues = Array.isArray(value) ? value : [];
                    const newValues = e.target.checked
                      ? [...currentValues, option]
                      : currentValues.filter((v) => v !== option);
                    updateStudentFormResponse(slotId, field.fieldId, newValues);
                  }}
                  className="rounded"
                />
                <span className="text-sm">{option}</span>
              </label>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  // Time-off calendar helper functions
  const getTimeOffsForDate = (date) => {
    if (!date) return [];
    return timeOffs.filter(timeOff => {
      // Fix timezone issue: normalize both dates to YYYY-MM-DD string format for comparison
      const timeOffDate = new Date(timeOff.date);

      // Format target date as YYYY-MM-DD using local date components
      const targetYear = date.getFullYear();
      const targetMonth = String(date.getMonth() + 1).padStart(2, '0');
      const targetDay = String(date.getDate()).padStart(2, '0');
      const targetDateStr = `${targetYear}-${targetMonth}-${targetDay}`;

      // Format time-off date as YYYY-MM-DD using local date components
      const timeOffYear = timeOffDate.getFullYear();
      const timeOffMonth = String(timeOffDate.getMonth() + 1).padStart(2, '0');
      const timeOffDay = String(timeOffDate.getDate()).padStart(2, '0');
      const timeOffDateStr = `${timeOffYear}-${timeOffMonth}-${timeOffDay}`;

      return timeOffDateStr === targetDateStr;
    });
  };

  const handleTimeOffDateClick = (date) => {
    // Always show the modal to let user choose which item to edit or add new
    setDayItemsModalDate(date);
    setShowDayItemsModal(true);
  };

  const getTimeOffCalendarDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const renderTimeOffCalendar = () => {
    const days = getTimeOffCalendarDays(currentMonth);
    const monthName = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

    return (
      <div className="space-y-4 md:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-2 sm:space-y-0">
          <h3 className="text-lg md:text-xl font-semibold">Time Off Calendar</h3>
          <div className="flex items-center justify-center sm:justify-end space-x-1">
            <button
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Previous month"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-base md:text-lg font-medium min-w-[180px] text-center px-2">
              {monthName}
            </span>
            <button
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Next month"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Add Time Off Button - Mobile Friendly */}
        <div className="sm:hidden">
          <button
            onClick={() => {
              setTimeOffForm({
                serviceId: '',
                date: new Date().toISOString().split('T')[0],
                isFullDay: true,
                usePredefinedSlots: false,
                fullDayReason: '',
                timeSlots: []
              });
              setEditingTimeOff(null);
              setShowTimeOffForm(true);
            }}
            className="w-full bg-blue-500 text-white px-4 py-3 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center space-x-2"
          >
            <Plus className="w-5 h-5" />
            <span>Add Time Off</span>
          </button>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center justify-end gap-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-green-100 border border-green-200"></div>
            <span className="text-gray-600">Bookings</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-orange-200 border border-orange-300"></div>
            <span className="text-gray-600">Time Off</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded bg-blue-100 border border-blue-200"></div>
            <span className="text-gray-600">Today</span>
          </div>
        </div>

        {/* Calendar */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-px bg-gray-200">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="bg-gray-50 p-2 md:p-3 text-center text-xs md:text-sm font-medium text-gray-700">
                <span className="hidden sm:inline">{day}</span>
                <span className="sm:hidden">{day.charAt(0)}</span>
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-px bg-gray-200">
            {days.map((day, index) => {
              const timeOffsForDay = day ? getTimeOffsForDate(day) : [];
              const bookingsForDay = day ? getBookingsForDate(day) : [];

              // Filter out customer bookings that have associated admin student bookings
              const filteredBookings = bookingsForDay.filter(booking => {
                const hasStudentBookingTimeOff = timeOffsForDay.some(timeOff =>
                  timeOff.isStudentBooking &&
                  timeOff.startTime === booking.startTime &&
                  timeOff.endTime === booking.endTime &&
                  (timeOff.serviceId._id || timeOff.serviceId) === booking.service._id
                );
                return !hasStudentBookingTimeOff;
              });

              // Combine bookings and time-offs into a single array for display
              const allItems = [
                ...filteredBookings.map(b => ({ ...b, type: 'booking' })),
                ...timeOffsForDay.map(t => ({ ...t, type: 'timeoff' }))
              ];

              // Sort all items by start time and status
              if (allItems.length > 0) {
                allItems.sort((a, b) => {
                  const now = new Date();
                  const isToday = day && day.toDateString() === now.toDateString();

                  // Get start times
                  const aStartTime = a.type === 'booking' ? a.startTime : (a.isFullDay ? '00:00' : a.startTime);
                  const bStartTime = b.type === 'booking' ? b.startTime : (b.isFullDay ? '00:00' : b.startTime);

                  // Get end times for checking if passed
                  const aEndTime = a.type === 'booking' ? a.endTime : (a.isFullDay ? '23:59' : a.endTime);
                  const bEndTime = b.type === 'booking' ? b.endTime : (b.isFullDay ? '23:59' : b.endTime);

                  // Check if items have passed
                  const aHasPassed = isToday && aEndTime ? (() => {
                    const [hours, minutes] = aEndTime.split(':').map(Number);
                    const aEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes);
                    return aEnd < now;
                  })() : false;

                  const bHasPassed = isToday && bEndTime ? (() => {
                    const [hours, minutes] = bEndTime.split(':').map(Number);
                    const bEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes);
                    return bEnd < now;
                  })() : false;

                  // Future/active items come first
                  if (aHasPassed !== bHasPassed) {
                    return aHasPassed ? 1 : -1;
                  }

                  // Within same status, sort by start time (earliest first)
                  if (aStartTime && bStartTime) {
                    return aStartTime.localeCompare(bStartTime);
                  }

                  return 0;
                });
              }

              const isToday = day && day.toDateString() === today.toDateString();
              const isPastDate = day && day < today;

              // For today, also check if we're past a reasonable time to add time-offs (e.g., current time)
              let isDisabled = !day || isPastDate;
              if (isToday) {
                // Allow adding time-offs for today, but with visual indication it's today
                isDisabled = !day;
              }

              return (
                <div
                  key={index}
                  className={`
                    min-h-[80px] md:min-h-[120px] p-1 md:p-2 bg-white relative
                    ${isToday ? 'bg-blue-50 ring-2 ring-blue-200' : ''}
                    ${isPastDate ? 'bg-gray-50 opacity-60 cursor-not-allowed' : ''}
                    ${!isDisabled ? 'cursor-pointer hover:bg-gray-50 active:bg-gray-100 touch-manipulation' : ''}
                    transition-colors
                  `}
                  onClick={() => !isDisabled && handleTimeOffDateClick(day)}
                >
                  {day && (
                    <>
                      {/* Date Number */}
                      <div className={`text-xs md:text-sm font-medium mb-1 ${isToday ? 'text-blue-600 font-bold' : isPastDate ? 'text-gray-400' : 'text-gray-900'}`}>
                        {day.getDate()}
                        {isToday && (
                          <div className="absolute top-1 right-1">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          </div>
                        )}
                      </div>

                      {/* Bookings and Time Off Entries */}
                      <div className="space-y-1">
                        {allItems.slice(0, 4).map((item, itemIndex) => {
                          // Check if this specific item has passed
                          let isItemPast = isPastDate;
                          const itemEndTime = item.type === 'booking' ? item.endTime : (item.isFullDay ? '23:59' : item.endTime);
                          if (isToday && itemEndTime) {
                            const now = new Date();
                            const [hours, minutes] = itemEndTime.split(':').map(Number);
                            const itemEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes);
                            isItemPast = itemEnd < now;
                          }

                          // Render booking
                          if (item.type === 'booking') {
                            return (
                              <div
                                key={`booking-${itemIndex}`}
                                className={`
                                  group relative text-xs rounded truncate transition-all pointer-events-none
                                  ${isItemPast
                                    ? 'bg-green-50 text-green-600 opacity-40'
                                    : 'bg-green-100 text-green-800 font-medium border border-green-200'
                                  }
                                `}
                                title={`${item.service?.name || 'Booking'} - ${item.startTime}-${item.endTime} - ${item.customerInfo?.firstName} ${item.customerInfo?.lastName}`}
                              >
                                <div className="flex items-center justify-between px-1">
                                  <span className="truncate flex-1 text-xs">
                                    {item.startTime}
                                  </span>
                                </div>
                                <div className="text-xs opacity-75 hidden sm:block px-1">
                                  {item.customerInfo?.firstName || 'Booking'}
                                </div>
                              </div>
                            );
                          }

                          // Render time-off
                          return (
                            <div
                              key={`timeoff-${itemIndex}`}
                              className={`
                                group relative text-xs rounded truncate transition-all pointer-events-none
                                ${item.isStudentBooking
                                  ? isItemPast
                                    ? 'bg-green-50 text-green-600'
                                    : 'bg-green-200 text-green-900 font-medium border border-green-300'
                                  : item.isFullDay
                                    ? isItemPast
                                      ? 'bg-red-50 text-red-600'
                                      : 'bg-red-200 text-red-900 font-medium border border-red-300'
                                    : isItemPast
                                      ? 'bg-orange-50 text-orange-600'
                                      : 'bg-orange-200 text-orange-900 font-medium border border-orange-300'
                                }
                                ${isItemPast ? 'opacity-40' : ''}
                              `}
                              title={`${item.serviceId?.name || 'Unknown Service'} - ${item.isFullDay ? 'Full Day' : item.startTime + '-' + item.endTime}${item.reason ? ' - ' + item.reason : ''}`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="truncate flex-1 text-xs">
                                  {item.isFullDay ? (
                                    <span className="hidden sm:inline">Full Day</span>
                                  ) : (
                                    <span>{item.startTime}</span>
                                  )}
                                </span>
                              </div>
                              {item.isStudentBooking && (
                                <div className="text-xs opacity-75 hidden sm:block">
                                  {item.studentInfo?.firstName || '[Student]'}
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {/* Show more indicator */}
                        {allItems.length > 4 && (
                          <div className="text-xs text-gray-500 text-center py-1">
                            +{allItems.length - 4} more
                          </div>
                        )}

                        {/* Empty state for clickable days */}
                        {allItems.length === 0 && !isDisabled && (
                          <div className="hidden md:flex items-center justify-center h-full opacity-0 hover:opacity-100 transition-opacity">
                            <Plus className="w-4 h-4 text-gray-400" />
                          </div>
                        )}
                      </div>

                      {/* Past date overlay */}
                      {isPastDate && (
                        <div className="absolute inset-0 bg-gray-100 bg-opacity-50 pointer-events-none"></div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Legend - Mobile Friendly */}
        <div className="flex flex-wrap gap-2 text-xs text-gray-600 justify-center sm:justify-start">
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-green-100 rounded"></div>
            <span>Student Bookings</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-red-100 rounded"></div>
            <span>Full Day Off</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-orange-100 rounded"></div>
            <span>Partial Time Off</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-blue-50 border border-blue-200 rounded"></div>
            <span>Today</span>
          </div>
        </div>

        {/* Desktop Add Button */}
        <div className="hidden sm:block">
          <button
            onClick={() => {
              setTimeOffForm({
                serviceId: '',
                date: new Date().toISOString().split('T')[0],
                isFullDay: true,
                usePredefinedSlots: false,
                fullDayReason: '',
                timeSlots: []
              });
              setEditingTimeOff(null);
              setShowTimeOffForm(true);
            }}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add Time Off</span>
          </button>
        </div>
      </div>
    );
  };

  const getPredefinedTimeSlots = () => {
    if (!timeOffForm.serviceId) return [];

    // Return only the specific time slots requested
    return [
      {
        id: 'predefined-06:00-07:30',
        startTime: '06:00',
        endTime: '07:30',
        duration: 90,
        label: '06:00 - 07:30'
      },
      {
        id: 'predefined-08:00-09:30',
        startTime: '08:00',
        endTime: '09:30',
        duration: 90,
        label: '08:00 - 09:30'
      },
      {
        id: 'predefined-10:00-11:30',
        startTime: '10:00',
        endTime: '11:30',
        duration: 90,
        label: '10:00 - 11:30'
      },
      {
        id: 'predefined-12:00-13:30',
        startTime: '12:00',
        endTime: '13:30',
        duration: 90,
        label: '12:00 - 13:30'
      },
      {
        id: 'predefined-14:00-15:30',
        startTime: '14:00',
        endTime: '15:30',
        duration: 90,
        label: '14:00 - 15:30'
      },
      {
        id: 'predefined-16:00-17:30',
        startTime: '16:00',
        endTime: '17:30',
        duration: 90,
        label: '16:00 - 17:30'
      }
    ];
  };

  const selectPredefinedSlot = (predefinedSlot) => {
    const newSlot = {
      id: Date.now(),
      startTime: predefinedSlot.startTime,
      endTime: predefinedSlot.endTime,
      reason: '',
      isStudentBooking: false,
      studentInfo: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        notes: '',
        formResponses: {}
      }
    };
    setTimeOffForm({
      ...timeOffForm,
      timeSlots: [...timeOffForm.timeSlots, newSlot]
    });
  };

  const handleCreateService = async () => {
    try {
      const token = localStorage.getItem("token");

      // Debug: Log the data being sent
      console.log("Sending service data:", serviceForm);

      if (editingService) {
        // Remove createdBy field when updating to avoid validation issues
        const updateData = { ...serviceForm };
        delete updateData.createdBy;
        delete updateData._id; // Also remove _id if present

        console.log("Update data (cleaned):", updateData);

        const response = await axios.put(
          `/api/admin/booking-services/${editingService._id}`,
          updateData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        console.log("Update response:", response.data);
      } else {
        const response = await axios.post("/api/admin/booking-services", serviceForm, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log("Create response:", response.data);
      }

      fetchServices();
      resetForm();
    } catch (error) {
      console.error("Error saving service:", error);
      console.error("Error response data:", error.response?.data);
      console.error("Error response status:", error.response?.status);
      alert(
        "Error saving service: " +
        (error.response?.data?.error || error.message)
      );
    }
  };

  const resetForm = () => {
    setServiceForm({
      name: "",
      slug: "",
      description: "",
      category: "driving_class",
      heroImage: "",
      formFields: [],
      availableDays: [],
      timeSlots: [
        {
          startTime: "09:00",
          endTime: "17:00",
          duration: 60,
          capacity: 1,
          buffer: 15,
          isDefault: false,
        },
      ],
      pricing: {
        basePrice: 0,
        pricingType: "fixed",
        pricePerMinute: 0,
        optionPricing: [],
      },
      advanceBookingDays: 30,
      minNoticeHours: 2,
      maxBookingsPerCustomer: 0,
      requiresPayment: false,
      paymentType: "full_upfront",
      depositAmount: 0,
      hasGST: false,
      hasPST: false,
      isActive: true,
    });
    setEditingService(null);
    setShowServiceForm(false);
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setServiceForm(service);
    setShowServiceForm(true);
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm("Are you sure you want to delete this service?"))
      return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/admin/booking-services/${serviceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchServices();
    } catch (error) {
      console.error("Error deleting service:", error);
      alert("Error deleting service");
    }
  };

  // Handle hero image upload
  const handleHeroImageUpload = (imageUrl) => {
    setServiceForm({ ...serviceForm, heroImage: imageUrl });
  };

  const addFormField = () => {
    if (!newField.fieldId || !newField.label) {
      alert("Please fill in field ID and label");
      return;
    }

    const updatedFields = [...serviceForm.formFields, { ...newField }];

    // Update optionPricing array for fields with options and prices
    let updatedOptionPricing = [...serviceForm.pricing.optionPricing];

    if ((newField.type === "select" || newField.type === "radio" || newField.type === "checkbox") &&
      newField.options && newField.optionPrices) {
      // Add pricing entries for each option and time slot
      newField.options.forEach((option, index) => {
        if (option && newField.optionPrices[index]) {
          const pricesForOption = newField.optionPrices[index];

          // Handle both old format (single price) and new format (object with duration keys)
          if (typeof pricesForOption === 'number') {
            // Old format - single price
            if (pricesForOption > 0) {
              updatedOptionPricing.push({
                fieldId: newField.fieldId,
                optionValue: option,
                additionalPrice: pricesForOption
              });
            }
          } else if (typeof pricesForOption === 'object') {
            // New format - prices per time slot duration
            Object.entries(pricesForOption).forEach(([duration, price]) => {
              if (price > 0) {
                updatedOptionPricing.push({
                  fieldId: newField.fieldId,
                  optionValue: option,
                  additionalPrice: price,
                  timeSlotDuration: parseInt(duration)
                });
              }
            });
          }
        }
      });
    }

    setServiceForm({
      ...serviceForm,
      formFields: updatedFields,
      pricing: {
        ...serviceForm.pricing,
        optionPricing: updatedOptionPricing
      }
    });

    setNewField({
      fieldId: "",
      label: "",
      type: "text",
      required: false,
      placeholder: "",
      options: [""],
      optionPrices: [{}],
      order: 0,
    });
  };

  const removeFormField = (index) => {
    const fieldToRemove = serviceForm.formFields[index];
    const updatedFields = serviceForm.formFields.filter((_, i) => i !== index);

    // Remove associated pricing entries
    const updatedOptionPricing = serviceForm.pricing.optionPricing.filter(
      pricing => pricing.fieldId !== fieldToRemove.fieldId
    );

    setServiceForm({
      ...serviceForm,
      formFields: updatedFields,
      pricing: {
        ...serviceForm.pricing,
        optionPricing: updatedOptionPricing
      }
    });
  };

  const startEditingField = (index) => {
    setEditingField(index);
    const field = serviceForm.formFields[index];

    // Load existing option prices from optionPricing array
    const optionPrices = [];
    if (field.options) {
      field.options.forEach((option, optionIndex) => {
        // Find all pricing entries for this option (could be multiple for different time slots)
        const pricingEntries = serviceForm.pricing.optionPricing.filter(
          p => p.fieldId === field.fieldId && p.optionValue === option
        );

        if (pricingEntries.length === 0) {
          optionPrices[optionIndex] = {};
        } else if (pricingEntries.length === 1 && !pricingEntries[0].timeSlotDuration) {
          // Old format - single price without time slot specification
          // Convert to new format by applying the same price to all time slots
          const pricesObj = {};
          const singlePrice = pricingEntries[0].additionalPrice;
          serviceForm.timeSlots.forEach(slot => {
            pricesObj[slot.duration] = singlePrice;
          });
          optionPrices[optionIndex] = pricesObj;
        } else {
          // New format - build object with duration keys
          const pricesObj = {};
          pricingEntries.forEach(entry => {
            if (entry.timeSlotDuration) {
              pricesObj[entry.timeSlotDuration] = entry.additionalPrice;
            }
          });
          optionPrices[optionIndex] = pricesObj;
        }
      });
    }

    setEditFieldData({
      ...field,
      optionPrices: optionPrices.length > 0 ? optionPrices : [{}]
    });
    setExpandedField(index); // Keep field expanded while editing
  };

  const cancelEditingField = () => {
    setEditingField(null);
    setEditFieldData({});
  };

  const saveEditingField = () => {
    if (!editFieldData.fieldId || !editFieldData.label) {
      alert("Please fill in field ID and label");
      return;
    }

    const updatedFields = [...serviceForm.formFields];
    const oldField = updatedFields[editingField];
    updatedFields[editingField] = { ...editFieldData };

    // Update optionPricing array
    let updatedOptionPricing = [...serviceForm.pricing.optionPricing];

    // Remove old pricing entries for this field
    updatedOptionPricing = updatedOptionPricing.filter(
      pricing => pricing.fieldId !== oldField.fieldId
    );

    // Add new pricing entries
    if ((editFieldData.type === "select" || editFieldData.type === "radio" || editFieldData.type === "checkbox") &&
      editFieldData.options && editFieldData.optionPrices) {
      editFieldData.options.forEach((option, index) => {
        if (option && editFieldData.optionPrices[index]) {
          const pricesForOption = editFieldData.optionPrices[index];

          // Handle both old format (single price) and new format (object with duration keys)
          if (typeof pricesForOption === 'number') {
            // Old format - single price
            if (pricesForOption > 0) {
              updatedOptionPricing.push({
                fieldId: editFieldData.fieldId,
                optionValue: option,
                additionalPrice: pricesForOption
              });
            }
          } else if (typeof pricesForOption === 'object') {
            // New format - prices per time slot duration
            Object.entries(pricesForOption).forEach(([duration, price]) => {
              if (price > 0) {
                updatedOptionPricing.push({
                  fieldId: editFieldData.fieldId,
                  optionValue: option,
                  additionalPrice: price,
                  timeSlotDuration: parseInt(duration)
                });
              }
            });
          }
        }
      });
    }

    setServiceForm({
      ...serviceForm,
      formFields: updatedFields,
      pricing: {
        ...serviceForm.pricing,
        optionPricing: updatedOptionPricing
      }
    });
    setEditingField(null);
    setEditFieldData({});
  };

  const updateEditFieldData = (field, value) => {
    setEditFieldData({ ...editFieldData, [field]: value });
  };

  const addEditFieldOption = () => {
    const updatedOptions = [...(editFieldData.options || []), ""];
    const updatedPrices = [...(editFieldData.optionPrices || []), {}];
    setEditFieldData({
      ...editFieldData,
      options: updatedOptions,
      optionPrices: updatedPrices
    });
  };

  const updateEditFieldOption = (index, value) => {
    const updatedOptions = [...editFieldData.options];
    updatedOptions[index] = value;
    setEditFieldData({ ...editFieldData, options: updatedOptions });
  };

  const updateEditFieldOptionPrice = (index, duration, price) => {
    const updatedPrices = [...(editFieldData.optionPrices || [])];

    // Ensure we have an object for this option
    if (!updatedPrices[index] || typeof updatedPrices[index] !== 'object') {
      updatedPrices[index] = {};
    }

    // Update the price for this specific duration
    updatedPrices[index][duration] = parseFloat(price) || 0;

    setEditFieldData({ ...editFieldData, optionPrices: updatedPrices });
  };

  const removeEditFieldOption = (index) => {
    const updatedOptions = editFieldData.options.filter((_, i) => i !== index);
    const updatedPrices = (editFieldData.optionPrices || []).filter((_, i) => i !== index);
    setEditFieldData({
      ...editFieldData,
      options: updatedOptions,
      optionPrices: updatedPrices
    });
  };

  const addTimeSlot = () => {
    const newSlot = {
      startTime: "09:00",
      endTime: "17:00",
      duration: 60,
      capacity: 1,
      buffer: 15,
      isDefault: false,
    };
    setServiceForm({
      ...serviceForm,
      timeSlots: [...serviceForm.timeSlots, newSlot],
    });
  };

  const updateTimeSlot = (index, field, value) => {
    const updatedSlots = [...serviceForm.timeSlots];

    if (field === "isDefault" && value) {
      // If setting this slot as default, unset all other defaults
      updatedSlots.forEach((slot, i) => {
        if (i !== index) {
          slot.isDefault = false;
        }
      });
    }

    updatedSlots[index][field] =
      field === "duration" || field === "capacity" || field === "buffer"
        ? parseInt(value)
        : field === "isDefault"
          ? Boolean(value)
          : value;
    setServiceForm({ ...serviceForm, timeSlots: updatedSlots });
  };

  const removeTimeSlot = (index) => {
    const updatedSlots = serviceForm.timeSlots.filter((_, i) => i !== index);
    setServiceForm({ ...serviceForm, timeSlots: updatedSlots });
  };

  const toggleDay = (day) => {
    const updatedDays = serviceForm.availableDays.includes(day)
      ? serviceForm.availableDays.filter((d) => d !== day)
      : [...serviceForm.availableDays, day];
    setServiceForm({ ...serviceForm, availableDays: updatedDays });
  };

  const handleEditBooking = (booking) => {
    console.log('Editing booking:', booking);
    setEditingBooking(booking);
    setShowEditBookingForm(true);
    setShowBookingDetails(false); // Close details modal if open
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm("Are you sure you want to delete this booking? This action cannot be undone.")) {
      return;
    }

    try {
      const token = localStorage.getItem("token");

      const response = await axios.delete(`/api/admin/bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Refresh both bookings and time-offs lists
      await fetchBookings();
      await fetchTimeOffs(); // Also refresh time-offs in case associated time-off was deleted

      // Close booking details modal if the deleted booking was selected
      if (selectedBooking && selectedBooking._id === bookingId) {
        setSelectedBooking(null);
        setShowBookingDetails(false);
      }

      // Show appropriate success message
      const message = response.data?.message || "Booking deleted successfully!";
      alert(message);
    } catch (error) {
      console.error("Error deleting booking:", error);
      alert("Error deleting booking: " + (error.response?.data?.error || error.message));
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      const token = localStorage.getItem("token");

      // Find the booking to get its details
      const booking = bookings.find(b => b._id === bookingId);

      await axios.put(
        `/api/admin/bookings/${bookingId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // If booking is cancelled, delete associated time-off entries
      if (newStatus === 'cancelled' && booking) {
        try {
          console.log('Looking for time-offs to delete for cancelled booking:', booking);
          console.log('Available time-offs:', timeOffs);

          // Find and delete matching time-off entries
          const matchingTimeOffs = timeOffs.filter(timeOff => {
            const timeOffDate = new Date(timeOff.date);
            const bookingDate = new Date(booking.bookingDate);

            // Normalize dates to compare (ignore time)
            timeOffDate.setHours(0, 0, 0, 0);
            bookingDate.setHours(0, 0, 0, 0);

            console.log('Comparing dates:', {
              timeOffDate: timeOffDate.toDateString(),
              bookingDate: bookingDate.toDateString(),
              timeOff: timeOff,
              booking: booking
            });

            // Check if dates match
            if (timeOffDate.getTime() !== bookingDate.getTime()) return false;

            // For partial day time-offs, check times and student booking flag
            if (!timeOff.isFullDay) {
              const matches = timeOff.startTime === booking.startTime &&
                timeOff.endTime === booking.endTime &&
                timeOff.isStudentBooking === true;
              console.log('Partial day match check:', matches, {
                timeOffStart: timeOff.startTime,
                bookingStart: booking.startTime,
                timeOffEnd: timeOff.endTime,
                bookingEnd: booking.endTime,
                isStudentBooking: timeOff.isStudentBooking
              });
              return matches;
            }

            // For full day time-offs, just check if it's a student booking
            const matches = timeOff.isStudentBooking === true;
            console.log('Full day match check:', matches, { isStudentBooking: timeOff.isStudentBooking });
            return matches;
          });

          console.log('Found matching time-offs to delete:', matchingTimeOffs);

          for (const timeOff of matchingTimeOffs) {
            console.log('Deleting time-off:', timeOff._id);
            await axios.delete(`/api/admin/time-off/${timeOff._id}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
          }

          if (matchingTimeOffs.length > 0) {
            fetchTimeOffs(); // Refresh time-offs if any were deleted
            console.log(`Successfully deleted ${matchingTimeOffs.length} associated time-off entries`);
          } else {
            console.log('No matching time-offs found to delete');
          }
        } catch (timeOffError) {
          console.error("Error deleting associated time-off:", timeOffError);
        }
      }

      fetchBookings();
    } catch (error) {
      console.error("Error updating booking status:", error);
    }
  };

  // Calendar functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const getBookingsForDate = (date) => {
    if (!date) return [];
    return bookings.filter(booking => {
      // Fix timezone issue: normalize both dates to YYYY-MM-DD string format for comparison
      // This avoids timezone conversion issues when comparing dates
      const bookingDate = new Date(booking.bookingDate);

      // Format target date as YYYY-MM-DD using local date components
      const targetYear = date.getFullYear();
      const targetMonth = String(date.getMonth() + 1).padStart(2, '0');
      const targetDay = String(date.getDate()).padStart(2, '0');
      const targetDateStr = `${targetYear}-${targetMonth}-${targetDay}`;

      // Format booking date as YYYY-MM-DD
      // The booking date is stored in Vancouver timezone, so we use local date components
      // which will correctly reflect the intended date in the user's local timezone
      const bookingYear = bookingDate.getFullYear();
      const bookingMonth = String(bookingDate.getMonth() + 1).padStart(2, '0');
      const bookingDay = String(bookingDate.getDate()).padStart(2, '0');
      const bookingDateStr = `${bookingYear}-${bookingMonth}-${bookingDay}`;

      return bookingDateStr === targetDateStr;
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'no_show':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatLocationName = (location) => {
    if (!location) return '';
    // Find location in the fetched locations array
    const locationObj = icbcLocations.find(loc => loc.key === location);
    return locationObj ? locationObj.name : location;
  };

  const handleReschedule = async (booking) => {
    setRescheduleBooking(booking);
    setRescheduleDate(new Date(booking.bookingDate).toISOString().split('T')[0]);
    setRescheduleTime(booking.startTime);
    setShowRescheduleModal(true);
  };

  const fetchAvailableTimeSlots = async (date, bookingId) => {
    if (!date || !bookingId) return;

    setLoadingTimeSlots(true);
    try {
      const token = localStorage.getItem("token");

      // Find the booking in the current state to get service info
      const booking = bookings.find(b => b._id === bookingId);
      if (!booking || !booking.service) {
        setAvailableTimeSlots([]);
        return;
      }

      // Use the correct availability endpoint
      const response = await axios.get(
        `/api/booking-services/${booking.service.slug}/availability/${date}?duration=${booking.duration}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.available) {
        setAvailableTimeSlots(response.data.slots);
      } else {
        setAvailableTimeSlots([]);
      }
    } catch (error) {
      console.error("Error fetching time slots:", error);
      setAvailableTimeSlots([]);
    } finally {
      setLoadingTimeSlots(false);
    }
  };

  const handleRescheduleSubmit = async () => {
    if (!rescheduleBooking || !rescheduleDate || !rescheduleTime) {
      alert("Please select a date and time");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `/api/admin/bookings/${rescheduleBooking._id}/reschedule`,
        {
          bookingDate: rescheduleDate,
          startTime: rescheduleTime,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Refresh bookings
      fetchBookings();
      setShowRescheduleModal(false);
      setRescheduleBooking(null);
      alert("Booking rescheduled successfully!");
    } catch (error) {
      console.error("Error rescheduling booking:", error);
      alert("Error rescheduling booking: " + (error.response?.data?.error || error.message));
    }
  };

  const renderServicesTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold">Booking Services</h3>
        <button
          onClick={() => setShowServiceForm(true)}
          className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-600"
        >
          <Plus className="w-4 h-4" />
          <span>New Service</span>
        </button>
      </div>

      {services.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No booking services created yet. Click "New Service" to get started.
        </div>
      ) : (
        <div className="grid gap-4">
          {services.map((service) => (
            <div key={service._id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    {service.heroImage && (
                      <img
                        src={service.heroImage}
                        alt={service.name}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    )}
                    <div>
                      <h4 className="text-lg font-semibold">{service.name}</h4>
                      <p className="text-gray-600 text-sm">
                        {service.description}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4 mt-3 text-sm text-gray-500">
                    <span className="flex items-center">
                      <Settings className="w-4 h-4 mr-1" />
                      {service.category.replace("_", " ")}
                    </span>
                    <span className="flex items-center">
                      <DollarSign className="w-4 h-4 mr-1" />
                      {service.requiresPayment
                        ? `${service.pricing.basePrice}`
                        : "Free"}
                    </span>
                    <span className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {service.timeSlots[0]?.duration || 60} min
                    </span>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditService(service)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteService(service._id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderBookingDetails = () => {
    console.log('renderBookingDetails called, selectedBooking:', selectedBooking);
    console.log('showBookingDetails:', showBookingDetails);

    if (!selectedBooking) {
      console.log('No selectedBooking, returning null');
      return null;
    }

    console.log('Rendering booking details modal');

    const getStatusColor = (status) => {
      switch (status) {
        case 'confirmed':
          return 'text-green-600 bg-green-100';
        case 'completed':
          return 'text-blue-600 bg-blue-100';
        case 'cancelled':
          return 'text-red-600 bg-red-100';
        case 'no_show':
          return 'text-orange-600 bg-orange-100';
        default:
          return 'text-gray-600 bg-gray-100';
      }
    };

    const getStatusIcon = (status) => {
      switch (status) {
        case 'confirmed':
          return <CheckCircle className="w-4 h-4" />;
        case 'completed':
          return <CheckCircle className="w-4 h-4" />;
        case 'cancelled':
          return <X className="w-4 h-4" />;
        case 'no_show':
          return <AlertCircle className="w-4 h-4" />;
        default:
          return <ClockIcon className="w-4 h-4" />;
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold">Booking Details</h3>
              <button
                onClick={() => {
                  setShowBookingDetails(false);
                  setSelectedBooking(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Booking Header */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="text-lg font-semibold">{selectedBooking.service?.name}</h4>
                    <p className="text-gray-600">Booking #{selectedBooking.bookingNumber}</p>
                  </div>
                  <div className="text-right">
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedBooking.status)}`}>
                      {getStatusIcon(selectedBooking.status)}
                      <span className="ml-1 capitalize">{selectedBooking.status.replace('_', ' ')}</span>
                    </div>
                    <p className="text-lg font-semibold mt-1">
                      ${selectedBooking.pricing?.totalAmount?.toFixed(2) || '0.00'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Customer Information */}
              <div className="border-t pt-6">
                <h4 className="text-lg font-medium mb-4 flex items-center">
                  <Users className="w-5 h-5 mr-2" />
                  Customer Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Full Name</label>
                      <p className="text-gray-900">{selectedBooking.customerInfo.firstName} {selectedBooking.customerInfo.lastName}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 flex items-center">
                        <Mail className="w-4 h-4 mr-1" />
                        Email
                      </label>
                      <p className="text-gray-900">{selectedBooking.customerInfo.email}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 flex items-center">
                        <Phone className="w-4 h-4 mr-1" />
                        Phone
                      </label>
                      <p className="text-gray-900">{selectedBooking.customerInfo.phone || 'Not provided'}</p>
                    </div>
                    {selectedBooking.customerInfo.location && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 flex items-center">
                          <MapPin className="w-4 h-4 mr-1" />
                          ICBC Test Location
                        </label>
                        <p className="text-gray-900">
                          {formatLocationName(selectedBooking.customerInfo.location)}
                        </p>
                      </div>
                    )}
                    {selectedBooking.customerInfo.company && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 flex items-center">
                          <Building className="w-4 h-4 mr-1" />
                          Company
                        </label>
                        <p className="text-gray-900">{selectedBooking.customerInfo.company}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Booking Details */}
              <div className="border-t pt-6">
                <h4 className="text-lg font-medium mb-4 flex items-center">
                  <Calendar className="w-5 h-5 mr-2" />
                  Booking Details
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Booking Date</label>
                      <p className="text-gray-900">
                        {new Date(selectedBooking.bookingDate).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          timeZone: 'America/Vancouver'
                        })}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Time</label>
                      <p className="text-gray-900">{selectedBooking.startTime} - {selectedBooking.endTime}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Duration</label>
                      <p className="text-gray-900">{selectedBooking.duration} minutes</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Created At</label>
                      <p className="text-gray-900">
                        {new Date(selectedBooking.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Last Updated</label>
                      <p className="text-gray-900">
                        {new Date(selectedBooking.updatedAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Responses */}
              {selectedBooking.formResponses && selectedBooking.formResponses.length > 0 && (
                <div className="border-t pt-6">
                  <h4 className="text-lg font-medium mb-4 flex items-center">
                    <FileText className="w-5 h-5 mr-2" />
                    Additional Information
                  </h4>
                  <div className="space-y-3">
                    {selectedBooking.formResponses.map((response, index) => (
                      <div key={index} className="bg-gray-50 p-3 rounded">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {response.label}
                        </label>
                        <p className="text-gray-900">
                          {Array.isArray(response.value) ? response.value.join(', ') : response.value}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Package Lessons - Show all bookings in the package */}
              {(() => {
                const bookingType = selectedBooking.formResponses?.find(r => r.fieldId === 'bookingType')?.value;
                const packageName = selectedBooking.formResponses?.find(r => r.fieldId === 'packageName')?.value;
                const packageLessons = selectedBooking.formResponses?.find(r => r.fieldId === 'packageLessons')?.value;

                if (bookingType === 'package' && packageName && packageLessons) {
                  // Find all related package bookings
                  const customerId = selectedBooking.customer?._id || selectedBooking.customer;
                  const serviceId = selectedBooking.service?._id || selectedBooking.service;
                  const customerEmail = selectedBooking.customerInfo?.email;

                  const relatedBookings = bookings.filter(b => {
                    const bPackageName = b.formResponses?.find(r => r.fieldId === 'packageName')?.value;
                    const bCustomerId = b.customer?._id || b.customer;
                    const bServiceId = b.service?._id || b.service;
                    const bCustomerEmail = b.customerInfo?.email;

                    // Match by customer (ID or email) and service and package name
                    const customerMatch = (customerId && bCustomerId && customerId === bCustomerId) ||
                      (customerEmail && bCustomerEmail && customerEmail === bCustomerEmail);
                    const serviceMatch = serviceId && bServiceId &&
                      (serviceId === bServiceId || serviceId.toString() === bServiceId.toString());
                    const packageMatch = bPackageName === packageName;

                    return customerMatch && serviceMatch && packageMatch;
                  }).sort((a, b) => {
                    const aLesson = parseInt(a.formResponses?.find(r => r.fieldId === 'lessonNumber')?.value) || 0;
                    const bLesson = parseInt(b.formResponses?.find(r => r.fieldId === 'lessonNumber')?.value) || 0;
                    return aLesson - bLesson;
                  });

                  if (relatedBookings.length > 1) {
                    return (
                      <div className="border-t pt-6">
                        <h4 className="text-lg font-medium mb-4 flex items-center">
                          <Calendar className="w-5 h-5 mr-2" />
                          Package Schedule ({relatedBookings.length} Lessons)
                        </h4>
                        <div className="space-y-2">
                          {relatedBookings.map((booking, index) => {
                            const lessonNumber = booking.formResponses?.find(r => r.fieldId === 'lessonNumber')?.value || (index + 1);
                            const isCurrentBooking = booking._id === selectedBooking._id;
                            return (
                              <div
                                key={booking._id}
                                className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${isCurrentBooking
                                    ? 'bg-blue-50 border-2 border-blue-300'
                                    : 'bg-gray-50 hover:bg-gray-100'
                                  }`}
                                onClick={() => {
                                  if (!isCurrentBooking) {
                                    setSelectedBooking(booking);
                                  }
                                }}
                              >
                                <div className="flex items-center space-x-3">
                                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${isCurrentBooking ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'
                                    }`}>
                                    {lessonNumber}
                                  </span>
                                  <div>
                                    <p className={`font-medium ${isCurrentBooking ? 'text-blue-700' : 'text-gray-900'}`}>
                                      Lesson {lessonNumber}
                                      {isCurrentBooking && <span className="ml-2 text-xs text-blue-500">(Current)</span>}
                                    </p>
                                    <p className="text-sm text-gray-600">
                                      {new Date(booking.bookingDate).toLocaleDateString('en-US', {
                                        weekday: 'short',
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                      })}
                                      {' • '}
                                      {booking.startTime} - {booking.endTime}
                                    </p>
                                  </div>
                                </div>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                                    booking.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                                      booking.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                        'bg-yellow-100 text-yellow-700'
                                  }`}>
                                  {booking.status}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }
                }
                return null;
              })()}

              {/* Payment Information */}
              {selectedBooking.pricing && (
                <div className="border-t pt-6">
                  <h4 className="text-lg font-medium mb-4 flex items-center">
                    <CreditCard className="w-5 h-5 mr-2" />
                    Payment Information
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Base Price:</span>
                        <span className="font-medium">${selectedBooking.pricing.basePrice?.toFixed(2) || '0.00'}</span>
                      </div>
                      {selectedBooking.pricing.optionPrices > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Options:</span>
                          <span className="font-medium">${selectedBooking.pricing.optionPrices?.toFixed(2) || '0.00'}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-600">Subtotal:</span>
                        <span className="font-medium">${selectedBooking.pricing.subtotal?.toFixed(2) || '0.00'}</span>
                      </div>
                      {selectedBooking.pricing.gst > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">GST (5%):</span>
                          <span className="font-medium">${selectedBooking.pricing.gst?.toFixed(2) || '0.00'}</span>
                        </div>
                      )}
                      {selectedBooking.pricing.pst > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">PST (7%):</span>
                          <span className="font-medium">${selectedBooking.pricing.pst?.toFixed(2) || '0.00'}</span>
                        </div>
                      )}
                      <div className="border-t pt-2 flex justify-between">
                        <span className="font-semibold">Total:</span>
                        <span className="font-semibold text-lg">${selectedBooking.pricing.totalAmount?.toFixed(2) || '0.00'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Status Update */}
              <div className="border-t pt-6">
                <h4 className="text-lg font-medium mb-4">Actions</h4>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                  <div className="flex items-center space-x-4">
                    <label className="text-sm font-medium text-gray-700">Status:</label>
                    <select
                      value={selectedBooking.status}
                      onChange={(e) => {
                        updateBookingStatus(selectedBooking._id, e.target.value);
                        setSelectedBooking({ ...selectedBooking, status: e.target.value });
                      }}
                      className="border rounded-lg px-4 py-2"
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="no_show">No Show</option>
                    </select>
                  </div>

                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => handleEditBooking(selectedBooking)}
                      className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                      <span>Edit Booking</span>
                    </button>
                    <button
                      onClick={() => handleDeleteBooking(selectedBooking._id)}
                      className="flex items-center space-x-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Delete Booking</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowBookingDetails(false);
                        setSelectedBooking(null);
                      }}
                      className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Edit form state
  const [editForm, setEditForm] = useState({
    customerInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: ''
    },
    status: 'pending',
    notes: ''
  });

  // Update edit form when editing booking changes
  useEffect(() => {
    if (editingBooking) {
      setEditForm({
        customerInfo: {
          firstName: editingBooking.customerInfo?.firstName || '',
          lastName: editingBooking.customerInfo?.lastName || '',
          email: editingBooking.customerInfo?.email || '',
          phone: editingBooking.customerInfo?.phone || '',
          company: editingBooking.customerInfo?.company || ''
        },
        status: editingBooking.status || 'pending',
        notes: editingBooking.notes || ''
      });
    }
  }, [editingBooking]);

  const renderEditBookingModal = () => {
    if (!editingBooking) return null;

    const handleSaveBooking = async () => {
      try {
        const token = localStorage.getItem("token");

        await axios.put(`/api/admin/bookings/${editingBooking._id}`, {
          customerInfo: editForm.customerInfo,
          status: editForm.status,
          notes: editForm.notes
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // Refresh both bookings and time-offs lists
        await fetchBookings();
        await fetchTimeOffs(); // Also refresh time-offs in case this was a student booking

        // Close modal
        setEditingBooking(null);
        setShowEditBookingForm(false);

        alert('Booking updated successfully!');
      } catch (error) {
        console.error('Error updating booking:', error);
        alert('Error updating booking: ' + (error.response?.data?.error || error.message));
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">Edit Booking</h2>
              <button
                onClick={() => {
                  setEditingBooking(null);
                  setShowEditBookingForm(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Booking Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium mb-2">{editingBooking.service?.name}</h3>
                <p className="text-sm text-gray-600">#{editingBooking.bookingNumber}</p>
                <p className="text-sm text-gray-600">
                  {new Date(editingBooking.bookingDate).toLocaleDateString()} at {editingBooking.startTime}
                </p>
              </div>

              {/* Customer Information */}
              <div>
                <h3 className="font-medium mb-4">Customer Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                    <input
                      type="text"
                      value={editForm.customerInfo.firstName}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        customerInfo: { ...editForm.customerInfo, firstName: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                    <input
                      type="text"
                      value={editForm.customerInfo.lastName}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        customerInfo: { ...editForm.customerInfo, lastName: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={editForm.customerInfo.email}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        customerInfo: { ...editForm.customerInfo, email: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={editForm.customerInfo.phone}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        customerInfo: { ...editForm.customerInfo, phone: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">At which ICBC Location Do You Want to Pick Up and Drop Off?</label>
                    <select
                      value={editForm.customerInfo.location || ''}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        customerInfo: { ...editForm.customerInfo, location: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select an ICBC location</option>
                      {icbcLocations.map((location) => (
                        <option key={location.key} value={location.key}>
                          {location.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                    <input
                      type="text"
                      value={editForm.customerInfo.company}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        customerInfo: { ...editForm.customerInfo, company: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="no_show">No Show</option>
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Add any notes about this booking..."
                />
              </div>

              {/* Actions */}
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={handleSaveBooking}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                >
                  <Save className="w-4 h-4" />
                  <span>Save Changes</span>
                </button>
                <button
                  onClick={() => {
                    setEditingBooking(null);
                    setShowEditBookingForm(false);
                  }}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderBookingsTab = () => {
    // Calculate filtered bookings count for display
    const filteredBookings = bookings.filter((booking) => {
      if (showArchivedBookings) {
        return true;
      }

      const now = new Date();
      const bookingDate = new Date(booking.bookingDate);

      // Set both dates to start of day for accurate comparison
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const bookingDay = new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate());

      if (bookingDay.getTime() > today.getTime()) {
        return true;
      }

      if (bookingDay.getTime() === today.getTime()) {
        const [hours, minutes] = booking.endTime.split(':').map(Number);
        const bookingEndTime = new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(), hours, minutes);
        return bookingEndTime > now;
      }

      return false;
    });

    return (
      <div className="space-y-4 md:space-y-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
          <h3 className="text-lg md:text-xl font-semibold">
            {showArchivedBookings ? 'All Bookings' : 'Active Bookings'}
          </h3>
          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
            {/* Archive Filter Toggle */}
            <div className="flex items-center space-x-2">
              <label className="flex items-center space-x-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={showArchivedBookings}
                  onChange={(e) => setShowArchivedBookings(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-gray-700">Show archived bookings</span>
              </label>
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={() => {
                  console.log('Manual refresh of bookings triggered');
                  fetchBookings();
                }}
                className="text-blue-600 hover:text-blue-800 text-sm flex items-center space-x-1 px-2 py-1 rounded hover:bg-blue-50"
                title="Refresh bookings"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span className="hidden sm:inline">Refresh</span>
              </button>
              <div className="text-sm text-gray-500">
                {filteredBookings.length} booking{filteredBookings.length !== 1 ? 's' : ''}
                {!showArchivedBookings && bookings.length > filteredBookings.length && (
                  <span className="ml-1 text-gray-400">
                    ({bookings.length - filteredBookings.length} archived)
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {bookings.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">No bookings yet</p>
            <p className="text-sm">Bookings will appear here once customers make appointments</p>
          </div>
        ) : (
          <div className="space-y-3 md:space-y-4">
            {bookings
              .filter((booking) => {
                // If showing archived bookings, show all
                if (showArchivedBookings) {
                  return true;
                }

                // Filter out bookings that have already passed
                const now = new Date();
                const bookingDate = new Date(booking.bookingDate);

                // Debug logging
                console.log('Filtering booking:', {
                  bookingId: booking._id,
                  bookingDate: booking.bookingDate,
                  parsedDate: bookingDate.toISOString(),
                  now: now.toISOString(),
                  isPast: bookingDate < now
                });

                // Set both dates to start of day for accurate comparison
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                const bookingDay = new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate());

                // If booking is on a future date, keep it
                if (bookingDay.getTime() > today.getTime()) {
                  return true;
                }

                // If booking is today, check if the time has passed
                if (bookingDay.getTime() === today.getTime()) {
                  const [hours, minutes] = booking.endTime.split(':').map(Number);
                  const bookingEndTime = new Date(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate(), hours, minutes);

                  // Keep booking if end time hasn't passed yet
                  return bookingEndTime > now;
                }

                // Past dates are filtered out
                return false;
              })
              .sort((a, b) => {
                // Sort by booking date (nearest first - ascending order)
                const dateA = new Date(a.bookingDate);
                const dateB = new Date(b.bookingDate);

                // If dates are the same, sort by start time (earliest first)
                if (dateA.getTime() === dateB.getTime()) {
                  const timeA = a.startTime.split(':').map(Number);
                  const timeB = b.startTime.split(':').map(Number);
                  const minutesA = timeA[0] * 60 + timeA[1];
                  const minutesB = timeB[0] * 60 + timeB[1];
                  return minutesA - minutesB; // Earliest time first
                }

                return dateA - dateB; // Nearest date first
              })
              .map((booking) => (
                <div
                  key={booking._id}
                  className="bg-white rounded-lg shadow-sm border p-4 md:p-6 cursor-pointer hover:shadow-md hover:border-blue-200 transition-all active:scale-[0.99]"
                  onClick={() => {
                    setSelectedBooking(booking);
                    setShowBookingDetails(true);
                  }}
                >
                  {/* Mobile Layout */}
                  <div className="md:hidden space-y-3">
                    {/* Header */}
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900 truncate">{booking.service?.name}</h4>
                        <p className="text-sm text-gray-500 truncate">#{booking.bookingNumber}</p>
                      </div>
                      <div className="ml-3 flex-shrink-0">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                          {booking.status.replace('_', ' ')}
                        </span>
                      </div>
                    </div>

                    {/* Customer Info */}
                    <div className="flex items-center space-x-2">
                      <Users className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {booking.customerInfo.firstName} {booking.customerInfo.lastName}
                      </span>
                    </div>

                    {/* Date & Time */}
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-sm text-gray-600">
                          {new Date(booking.bookingDate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            timeZone: 'America/Vancouver'
                          })}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-sm text-gray-600">
                          {booking.startTime}
                        </span>
                      </div>
                    </div>

                    {/* Contact Info */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 min-w-0 flex-1">
                        <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-sm text-gray-600 truncate">
                          {booking.customerInfo.phone || 'No phone'}
                        </span>
                      </div>
                      <div className="text-sm font-semibold text-gray-900 ml-2">
                        ${booking.pricing.totalAmount.toFixed(2)}
                      </div>
                    </div>

                    {/* Actions - Mobile */}
                    <div className="pt-2 border-t space-y-2">
                      <select
                        value={booking.status}
                        onChange={(e) => updateBookingStatus(booking._id, e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                        onClick={e => e.stopPropagation()}
                      >
                        <option value="pending">Pending</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="no_show">No Show</option>
                      </select>
                      <div className="flex space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditBooking(booking);
                          }}
                          className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                        >
                          <Edit className="w-4 h-4" />
                          <span>Edit</span>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteBooking(booking._id);
                          }}
                          className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span>Delete</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Desktop Layout */}
                  <div className="hidden md:block">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3 mb-2">
                          <h4 className="font-semibold text-gray-900">{booking.service?.name}</h4>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                            {booking.status.replace('_', ' ')}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mb-3">#{booking.bookingNumber}</p>

                        {/* Desktop Info Grid */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                          <div className="flex items-center space-x-2">
                            <Users className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-900 font-medium">
                              {booking.customerInfo.firstName} {booking.customerInfo.lastName}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-600">
                              {new Date(booking.bookingDate).toLocaleDateString('en-US', {
                                timeZone: 'America/Vancouver'
                              })}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Clock className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-600">
                              {booking.startTime} - {booking.endTime}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-600">
                              {booking.customerInfo.phone || 'No phone'}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="ml-6 flex items-center space-x-4">
                        <div className="text-right">
                          <div className="text-lg font-semibold text-gray-900">
                            ${booking.pricing.totalAmount.toFixed(2)}
                          </div>
                        </div>
                        <select
                          value={booking.status}
                          onChange={(e) => updateBookingStatus(booking._id, e.target.value)}
                          className="border rounded-lg px-4 py-2 text-sm bg-white min-w-[120px]"
                          onClick={e => e.stopPropagation()}
                        >
                          <option value="pending">Pending</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                          <option value="no_show">No Show</option>
                        </select>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditBooking(booking);
                          }}
                          className="flex items-center space-x-1 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm mr-2"
                          title="Edit booking"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteBooking(booking._id);
                          }}
                          className="flex items-center space-x-1 px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm"
                          title="Delete booking"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    );
  };

  const renderServiceForm = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold">
              {editingService ? "Edit Service" : "Create New Service"}
            </h3>
            <button
              onClick={resetForm}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Service Name
                </label>
                <input
                  type="text"
                  value={serviceForm.name}
                  onChange={(e) =>
                    setServiceForm({ ...serviceForm, name: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="e.g., Driving Lesson Class 5"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  URL Slug
                </label>
                <input
                  type="text"
                  value={serviceForm.slug}
                  onChange={(e) =>
                    setServiceForm({ ...serviceForm, slug: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="driving-lesson-class-5"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">
                  Description
                </label>
                <textarea
                  value={serviceForm.description}
                  onChange={(e) =>
                    setServiceForm({
                      ...serviceForm,
                      description: e.target.value,
                    })
                  }
                  className="w-full border rounded-lg px-3 py-2"
                  rows="3"
                  placeholder="Describe your service..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">
                  Category
                </label>
                <select
                  value={serviceForm.category}
                  onChange={(e) =>
                    setServiceForm({ ...serviceForm, category: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-2"
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Hero Image Section */}
            <div className="border-t pt-6">
              <h4 className="text-lg font-medium mb-4 flex items-center">
                <ImageIcon className="w-5 h-5 mr-2" />
                Hero Image
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Image URL
                  </label>
                  <input
                    type="url"
                    value={serviceForm.heroImage}
                    onChange={(e) =>
                      setServiceForm({
                        ...serviceForm,
                        heroImage: e.target.value,
                      })
                    }
                    className="w-full border rounded-lg px-3 py-2"
                    placeholder="https://example.com/image.jpg"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Enter an image URL or upload a file below
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Upload Image
                  </label>
                  <ImageUploader
                    onImageUpload={handleHeroImageUpload}
                    pageSlug={`booking-${serviceForm.slug || "service"}`}
                  />
                </div>
              </div>

              {/* Image Preview */}
              {serviceForm.heroImage && (
                <div className="mt-4">
                  <label className="block text-sm font-medium mb-2">
                    Preview
                  </label>
                  <div className="relative">
                    <img
                      src={serviceForm.heroImage}
                      alt="Hero image preview"
                      className="w-full h-48 object-cover rounded-lg border"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setServiceForm({ ...serviceForm, heroImage: "" })
                      }
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Available Days */}
            <div className="border-t pt-6">
              <label className="block text-sm font-medium mb-2">
                Available Days
              </label>
              <div className="flex flex-wrap gap-2">
                {daysOfWeek.map((day) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDay(day)}
                    className={`px-3 py-1 rounded-full text-sm ${serviceForm.availableDays.includes(day)
                      ? "bg-blue-500 text-white"
                      : "bg-gray-200 text-gray-700"
                      }`}
                  >
                    {day.charAt(0).toUpperCase() + day.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Time Slots */}
            <div className="border-t pt-6">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium">Time Slots</label>
                <button
                  type="button"
                  onClick={addTimeSlot}
                  className="text-blue-500 hover:text-blue-700 text-sm"
                >
                  + Add Slot
                </button>
              </div>
              <div className="space-y-2">
                {serviceForm.timeSlots.map((slot, index) => (
                  <div key={index} className="p-3 border rounded">
                    <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
                      <div>
                        <input
                          type="time"
                          value={slot.startTime}
                          onChange={(e) =>
                            updateTimeSlot(index, "startTime", e.target.value)
                          }
                          className="border rounded px-2 py-1 w-full"
                        />
                        <p className="text-xs text-gray-500 mt-1">Start Time</p>
                      </div>
                      <div>
                        <input
                          type="time"
                          value={slot.endTime}
                          onChange={(e) =>
                            updateTimeSlot(index, "endTime", e.target.value)
                          }
                          className="border rounded px-2 py-1 w-full"
                        />
                        <p className="text-xs text-gray-500 mt-1">End Time</p>
                      </div>
                      <div>
                        <input
                          type="number"
                          value={slot.duration}
                          onChange={(e) =>
                            updateTimeSlot(index, "duration", e.target.value)
                          }
                          placeholder="Duration (min)"
                          className="border rounded px-2 py-1 w-full"
                        />
                        <p className="text-xs text-gray-500 mt-1">Duration</p>
                      </div>
                      <div>
                        <input
                          type="number"
                          value={slot.buffer}
                          onChange={(e) =>
                            updateTimeSlot(index, "buffer", e.target.value)
                          }
                          placeholder="Buffer (min)"
                          className="border rounded px-2 py-1 w-full"
                        />
                        <p className="text-xs text-gray-500 mt-1">Buffer</p>
                      </div>
                      <div>
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={slot.isDefault || false}
                            onChange={(e) =>
                              updateTimeSlot(index, "isDefault", e.target.checked)
                            }
                            className="rounded"
                          />
                          <span className="text-sm">Default</span>
                        </label>
                        <p className="text-xs text-gray-500 mt-1">Show first</p>
                      </div>
                      <div className="flex items-start justify-center lg:justify-start">
                        {serviceForm.timeSlots.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeTimeSlot(index)}
                            className="text-red-500 hover:text-red-700 mt-2"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Payment Settings */}
            <div className="border-t pt-6">
              <h4 className="text-lg font-medium mb-4">Payment Settings</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={serviceForm.requiresPayment}
                      onChange={(e) =>
                        setServiceForm({
                          ...serviceForm,
                          requiresPayment: e.target.checked,
                        })
                      }
                      className="rounded"
                    />
                    <span>Requires Payment</span>
                  </label>
                </div>
                {serviceForm.requiresPayment && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Pricing Type
                      </label>
                      <select
                        value={serviceForm.pricing.pricingType}
                        onChange={(e) =>
                          setServiceForm({
                            ...serviceForm,
                            pricing: {
                              ...serviceForm.pricing,
                              pricingType: e.target.value,
                            },
                          })
                        }
                        className="w-full border rounded-lg px-3 py-2"
                      >
                        <option value="fixed">Fixed Price</option>
                        <option value="duration_based">Duration Based</option>
                        <option value="free">Free</option>
                      </select>
                    </div>

                    {serviceForm.pricing.pricingType === "fixed" && (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium mb-2">
                          Fixed Price for Each Time Slot
                        </label>
                        <div className="space-y-3">
                          {serviceForm.timeSlots.map((slot, index) => {
                            // Find existing price for this time slot
                            const existingPrice = serviceForm.pricing.optionPricing.find(
                              p => p.fieldId === 'timeSlot' && p.optionValue === `${slot.duration}min`
                            );

                            return (
                              <div key={index} className="flex items-center space-x-3 p-3 border rounded-lg">
                                <div className="flex-1">
                                  <span className="font-medium">{slot.duration} minutes</span>
                                  <span className="text-gray-500 ml-2">
                                    ({slot.startTime} - {slot.endTime})
                                  </span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm text-gray-500">$</span>
                                  <input
                                    type="number"
                                    value={existingPrice ? existingPrice.additionalPrice : 0}
                                    onChange={(e) => {
                                      const price = parseFloat(e.target.value) || 0;
                                      const updatedOptionPricing = [...serviceForm.pricing.optionPricing];

                                      // Remove existing price for this time slot
                                      const filteredPricing = updatedOptionPricing.filter(
                                        p => !(p.fieldId === 'timeSlot' && p.optionValue === `${slot.duration}min`)
                                      );

                                      // Add new price if greater than 0
                                      if (price > 0) {
                                        filteredPricing.push({
                                          fieldId: 'timeSlot',
                                          optionValue: `${slot.duration}min`,
                                          additionalPrice: price
                                        });
                                      }

                                      setServiceForm({
                                        ...serviceForm,
                                        pricing: {
                                          ...serviceForm.pricing,
                                          optionPricing: filteredPricing
                                        }
                                      });
                                    }}
                                    className="w-24 border rounded px-2 py-1 text-sm"
                                    placeholder="0.00"
                                    min="0"
                                    step="0.01"
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Set the fixed price for each time slot duration. Leave at 0 to use base price.
                        </p>
                      </div>
                    )}

                    {serviceForm.pricing.pricingType !== "fixed" && (
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Base Price ($)
                        </label>
                        <input
                          type="number"
                          value={serviceForm.pricing.basePrice}
                          onChange={(e) =>
                            setServiceForm({
                              ...serviceForm,
                              pricing: {
                                ...serviceForm.pricing,
                                basePrice: parseFloat(e.target.value) || 0,
                              },
                            })
                          }
                          className="w-full border rounded-lg px-3 py-2"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    )}

                    {serviceForm.pricing.pricingType === "duration_based" && (
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Price per hour ($)
                        </label>
                        <input
                          type="number"
                          value={serviceForm.pricing.pricePerMinute}
                          onChange={(e) =>
                            setServiceForm({
                              ...serviceForm,
                              pricing: {
                                ...serviceForm.pricing,
                                pricePerMinute: parseFloat(e.target.value) || 0,
                              },
                            })
                          }
                          className="w-full border rounded-lg px-3 py-2"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    )}
                    <div>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={serviceForm.hasGST}
                          onChange={(e) =>
                            setServiceForm({
                              ...serviceForm,
                              hasGST: e.target.checked,
                            })
                          }
                          className="rounded"
                        />
                        <span>Add GST (5%)</span>
                      </label>
                    </div>
                    <div>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={serviceForm.hasPST}
                          onChange={(e) =>
                            setServiceForm({
                              ...serviceForm,
                              hasPST: e.target.checked,
                            })
                          }
                          className="rounded"
                        />
                        <span>Add PST (7%)</span>
                      </label>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Booking Settings */}
            <div className="border-t pt-6">
              <h4 className="text-lg font-medium mb-4">Booking Settings</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Advance Booking Days
                  </label>
                  <input
                    type="number"
                    value={serviceForm.advanceBookingDays}
                    onChange={(e) =>
                      setServiceForm({
                        ...serviceForm,
                        advanceBookingDays: parseInt(e.target.value) || 30,
                      })
                    }
                    className="w-full border rounded-lg px-3 py-2"
                    min="1"
                    max="365"
                    placeholder="30"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    How many days in advance customers can book (default: 30 days)
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Minimum Notice Hours
                  </label>
                  <input
                    type="number"
                    value={serviceForm.minNoticeHours}
                    onChange={(e) =>
                      setServiceForm({
                        ...serviceForm,
                        minNoticeHours: parseInt(e.target.value) || 2,
                      })
                    }
                    className="w-full border rounded-lg px-3 py-2"
                    min="0"
                    max="168"
                    placeholder="2"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Minimum hours notice required before booking (default: 2 hours)
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Max Bookings per Customer
                  </label>
                  <input
                    type="number"
                    value={serviceForm.maxBookingsPerCustomer}
                    onChange={(e) =>
                      setServiceForm({
                        ...serviceForm,
                        maxBookingsPerCustomer: parseInt(e.target.value) || 0,
                      })
                    }
                    className="w-full border rounded-lg px-3 py-2"
                    min="0"
                    max="100"
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Maximum bookings per customer (0 = unlimited)
                  </p>
                </div>
              </div>
            </div>

            {/* Form Fields */}
            <div className="border-t pt-6">
              <h4 className="text-lg font-medium mb-4">Custom Form Fields</h4>

              {/* Add New Field */}
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h5 className="font-medium mb-3">Add New Field</h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input
                    type="text"
                    placeholder="Field ID (e.g., class_type)"
                    value={newField.fieldId}
                    onChange={(e) =>
                      setNewField({ ...newField, fieldId: e.target.value })
                    }
                    className="border rounded px-3 py-2"
                  />
                  <input
                    type="text"
                    placeholder="Field Label"
                    value={newField.label}
                    onChange={(e) =>
                      setNewField({ ...newField, label: e.target.value })
                    }
                    className="border rounded px-3 py-2"
                  />
                  <select
                    value={newField.type}
                    onChange={(e) =>
                      setNewField({ ...newField, type: e.target.value })
                    }
                    className="border rounded px-3 py-2"
                  >
                    {fieldTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {(newField.type === "select" ||
                  newField.type === "radio" ||
                  newField.type === "checkbox") && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium mb-2">
                        Options
                      </label>
                      {newField.options.map((option, index) => (
                        <div
                          key={index}
                          className="border rounded-lg p-3 mb-3 bg-white"
                        >
                          <div className="flex items-center space-x-2 mb-2">
                            <input
                              type="text"
                              value={option}
                              onChange={(e) => {
                                const updatedOptions = [...newField.options];
                                updatedOptions[index] = e.target.value;
                                setNewField({
                                  ...newField,
                                  options: updatedOptions,
                                });
                              }}
                              className="flex-1 border rounded px-3 py-2"
                              placeholder="Option text"
                            />
                            {newField.options.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const updatedOptions = newField.options.filter(
                                    (_, i) => i !== index
                                  );
                                  const updatedPrices = (newField.optionPrices || []).filter(
                                    (_, i) => i !== index
                                  );
                                  setNewField({
                                    ...newField,
                                    options: updatedOptions,
                                    optionPrices: updatedPrices,
                                  });
                                }}
                                className="text-red-500 hover:text-red-700"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>

                          {/* Pricing for each time slot */}
                          <div className="ml-4 space-y-1">
                            <label className="text-xs font-medium text-gray-600">Extra Charges by Time Slot:</label>
                            {serviceForm.timeSlots.map((slot, slotIndex) => {
                              const currentPrice = (newField.optionPrices[index] && typeof newField.optionPrices[index] === 'object')
                                ? newField.optionPrices[index][slot.duration] || 0
                                : 0;

                              return (
                                <div key={slotIndex} className="flex items-center space-x-2">
                                  <span className="text-xs text-gray-600 w-32">
                                    {slot.duration} min slot:
                                  </span>
                                  <div className="flex items-center space-x-1">
                                    <span className="text-xs text-gray-500">+$</span>
                                    <input
                                      type="number"
                                      value={currentPrice}
                                      onChange={(e) => {
                                        const updatedPrices = [...(newField.optionPrices || [])];

                                        // Ensure we have an object for this option
                                        if (!updatedPrices[index] || typeof updatedPrices[index] !== 'object') {
                                          updatedPrices[index] = {};
                                        }

                                        // Update the price for this specific duration
                                        updatedPrices[index][slot.duration] = parseFloat(e.target.value) || 0;

                                        setNewField({
                                          ...newField,
                                          optionPrices: updatedPrices,
                                        });
                                      }}
                                      className="w-20 border rounded px-2 py-1 text-xs"
                                      placeholder="0.00"
                                      min="0"
                                      step="0.01"
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() =>
                          setNewField({
                            ...newField,
                            options: [...newField.options, ""],
                            optionPrices: [...(newField.optionPrices || []), {}],
                          })
                        }
                        className="text-blue-500 hover:text-blue-700 text-sm"
                      >
                        + Add Option
                      </button>
                    </div>
                  )}

                <div className="flex items-center space-x-4 mt-3">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={newField.required}
                      onChange={(e) =>
                        setNewField({ ...newField, required: e.target.checked })
                      }
                      className="rounded"
                    />
                    <span className="text-sm">Required</span>
                  </label>
                  <button
                    type="button"
                    onClick={addFormField}
                    className="bg-blue-500 text-white px-3 py-1 rounded text-sm hover:bg-blue-600"
                  >
                    Add Field
                  </button>
                </div>
              </div>

              {/* Existing Fields */}
              {serviceForm.formFields.length > 0 && (
                <div className="space-y-2">
                  <h5 className="font-medium">Current Fields:</h5>
                  {serviceForm.formFields.map((field, index) => (
                    <div key={index} className="border rounded overflow-hidden">
                      <div
                        className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => setExpandedField(expandedField === index ? null : index)}
                      >
                        <div className="flex-1">
                          <span className="font-medium">{field.label}</span>
                          <span className="text-gray-500 ml-2">
                            ({field.type})
                          </span>
                          {field.required && (
                            <span className="text-red-500 ml-1">*</span>
                          )}
                          {field.placeholder && (
                            <span className="text-gray-400 ml-2 text-sm">
                              - {field.placeholder}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              startEditingField(index);
                            }}
                            className="text-blue-500 hover:text-blue-700 p-1"
                            title="Edit field"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFormField(index);
                            }}
                            className="text-red-500 hover:text-red-700 p-1"
                            title="Delete field"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          <div className="text-gray-400">
                            {expandedField === index ? (
                              <ChevronRight className="w-4 h-4 transform rotate-90 transition-transform" />
                            ) : (
                              <ChevronRight className="w-4 h-4 transition-transform" />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {expandedField === index && (
                        <div className="px-3 pb-3 bg-gray-50 border-t">
                          {editingField === index ? (
                            /* Edit Mode */
                            <div className="mt-3">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Field ID
                                  </label>
                                  <input
                                    type="text"
                                    value={editFieldData.fieldId || ""}
                                    onChange={(e) => updateEditFieldData("fieldId", e.target.value)}
                                    className="w-full text-sm border rounded px-2 py-1"
                                    placeholder="e.g., class_type"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Label
                                  </label>
                                  <input
                                    type="text"
                                    value={editFieldData.label || ""}
                                    onChange={(e) => updateEditFieldData("label", e.target.value)}
                                    className="w-full text-sm border rounded px-2 py-1"
                                    placeholder="Field Label"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Type
                                  </label>
                                  <select
                                    value={editFieldData.type || "text"}
                                    onChange={(e) => updateEditFieldData("type", e.target.value)}
                                    className="w-full text-sm border rounded px-2 py-1"
                                  >
                                    {fieldTypes.map((type) => (
                                      <option key={type.value} value={type.value}>
                                        {type.label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Placeholder
                                  </label>
                                  <input
                                    type="text"
                                    value={editFieldData.placeholder || ""}
                                    onChange={(e) => updateEditFieldData("placeholder", e.target.value)}
                                    className="w-full text-sm border rounded px-2 py-1"
                                    placeholder="Placeholder text"
                                  />
                                </div>
                              </div>

                              {/* Options for select/radio/checkbox */}
                              {(editFieldData.type === "select" ||
                                editFieldData.type === "radio" ||
                                editFieldData.type === "checkbox") && (
                                  <div className="mt-3">
                                    <label className="block text-xs font-medium text-gray-600 mb-2">
                                      Options
                                    </label>
                                    {(editFieldData.options || []).map((option, optionIndex) => (
                                      <div key={optionIndex} className="border rounded-lg p-2 mb-3 bg-gray-50">
                                        <div className="flex items-center space-x-2 mb-2">
                                          <input
                                            type="text"
                                            value={option}
                                            onChange={(e) => updateEditFieldOption(optionIndex, e.target.value)}
                                            className="flex-1 text-sm border rounded px-2 py-1"
                                            placeholder="Option text"
                                          />
                                          {(editFieldData.options || []).length > 1 && (
                                            <button
                                              type="button"
                                              onClick={() => removeEditFieldOption(optionIndex)}
                                              className="text-red-500 hover:text-red-700"
                                            >
                                              <X className="w-4 h-4" />
                                            </button>
                                          )}
                                        </div>

                                        {/* Pricing for each time slot */}
                                        <div className="ml-3 space-y-1">
                                          <label className="text-xs font-medium text-gray-600">Extra Charges by Time Slot:</label>
                                          {serviceForm.timeSlots.map((slot, slotIndex) => {
                                            const currentPrice = (editFieldData.optionPrices[optionIndex] && typeof editFieldData.optionPrices[optionIndex] === 'object')
                                              ? editFieldData.optionPrices[optionIndex][slot.duration] || 0
                                              : 0;

                                            return (
                                              <div key={slotIndex} className="flex items-center space-x-2">
                                                <span className="text-xs text-gray-600 w-28">
                                                  {slot.duration} min slot:
                                                </span>
                                                <div className="flex items-center space-x-1">
                                                  <span className="text-xs text-gray-500">+$</span>
                                                  <input
                                                    type="number"
                                                    value={currentPrice}
                                                    onChange={(e) => updateEditFieldOptionPrice(optionIndex, slot.duration, e.target.value)}
                                                    className="w-16 text-xs border rounded px-1 py-1"
                                                    placeholder="0.00"
                                                    min="0"
                                                    step="0.01"
                                                  />
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    ))}
                                    <button
                                      type="button"
                                      onClick={addEditFieldOption}
                                      className="text-blue-500 hover:text-blue-700 text-xs"
                                    >
                                      + Add Option
                                    </button>
                                  </div>
                                )}

                              {/* Settings */}
                              <div className="mt-3">
                                <label className="block text-xs font-medium text-gray-600 mb-2">
                                  Settings
                                </label>
                                <div className="flex items-center space-x-4">
                                  <label className="flex items-center space-x-1">
                                    <input
                                      type="checkbox"
                                      checked={editFieldData.required || false}
                                      onChange={(e) => updateEditFieldData("required", e.target.checked)}
                                      className="rounded"
                                    />
                                    <span className="text-xs">Required</span>
                                  </label>
                                  <div className="flex items-center space-x-1">
                                    <label className="text-xs">Order:</label>
                                    <input
                                      type="number"
                                      value={editFieldData.order || 0}
                                      onChange={(e) => updateEditFieldData("order", parseInt(e.target.value) || 0)}
                                      className="w-16 text-xs border rounded px-1 py-1"
                                      min="0"
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Action Buttons */}
                              <div className="flex justify-end space-x-2 mt-4 pt-3 border-t">
                                <button
                                  type="button"
                                  onClick={cancelEditingField}
                                  className="px-3 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={saveEditingField}
                                  className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                                >
                                  Save Changes
                                </button>
                              </div>
                            </div>
                          ) : (
                            /* View Mode */
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Field ID
                                </label>
                                <p className="text-sm text-gray-800 font-mono bg-white px-2 py-1 rounded border">
                                  {field.fieldId}
                                </p>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Type
                                </label>
                                <p className="text-sm text-gray-800 bg-white px-2 py-1 rounded border">
                                  {fieldTypes.find(t => t.value === field.type)?.label || field.type}
                                </p>
                              </div>
                              {field.placeholder && (
                                <div className="md:col-span-2">
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Placeholder
                                  </label>
                                  <p className="text-sm text-gray-800 bg-white px-2 py-1 rounded border">
                                    {field.placeholder}
                                  </p>
                                </div>
                              )}
                              {field.options && field.options.length > 0 && field.options[0] !== "" && (
                                <div className="md:col-span-2">
                                  <label className="block text-xs font-medium text-gray-600 mb-1">
                                    Options
                                  </label>
                                  <div className="bg-white p-2 rounded border space-y-2">
                                    {field.options.map((option, optionIndex) => {
                                      // Get all pricing entries for this option (might have multiple for different time slots)
                                      const pricingEntries = serviceForm.pricing.optionPricing.filter(
                                        p => p.fieldId === field.fieldId && p.optionValue === option
                                      );

                                      const hasTimeSlotPricing = pricingEntries.some(p => p.timeSlotDuration);

                                      return (
                                        <div key={optionIndex} className="border-l-2 border-blue-300 pl-2">
                                          <div className="font-medium text-sm">{option}</div>
                                          {pricingEntries.length > 0 ? (
                                            <div className="ml-2 mt-1 space-y-0.5">
                                              {hasTimeSlotPricing ? (
                                                // Show time-slot-specific pricing
                                                pricingEntries.map((entry, idx) => (
                                                  <div key={idx} className="text-xs text-gray-600">
                                                    {entry.timeSlotDuration} min slot:
                                                    <span className="ml-1 font-semibold text-green-700">
                                                      +${entry.additionalPrice.toFixed(2)}
                                                    </span>
                                                  </div>
                                                ))
                                              ) : (
                                                // Show single price (legacy format)
                                                <div className="text-xs text-gray-600">
                                                  All slots:
                                                  <span className="ml-1 font-semibold text-green-700">
                                                    +${pricingEntries[0].additionalPrice.toFixed(2)}
                                                  </span>
                                                </div>
                                              )}
                                            </div>
                                          ) : (
                                            <div className="text-xs text-gray-500 ml-2 mt-1">No extra charge</div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              <div className="md:col-span-2">
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Settings
                                </label>
                                <div className="flex space-x-4 text-sm">
                                  <span className={`px-2 py-1 rounded text-xs ${field.required ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'}`}>
                                    {field.required ? 'Required' : 'Optional'}
                                  </span>
                                  <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
                                    Order: {field.order || 0}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateService}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>
                  {editingService ? "Update Service" : "Create Service"}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCalendarTab = () => {
    const days = getDaysInMonth(currentMonth);
    const monthName = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    return (
      <div className="space-y-4 md:space-y-6">
        {/* Header - Mobile Friendly */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
          <h3 className="text-lg md:text-xl font-semibold">Calendar View</h3>

          {/* Navigation - Larger touch targets for mobile */}
          <div className="flex items-center justify-center sm:justify-end space-x-1">
            <button
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}
              className="p-3 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors touch-manipulation"
              aria-label="Previous month"
            >
              <ChevronLeft className="w-5 h-5 sm:w-4 sm:h-4" />
            </button>
            <div className="min-w-[200px] text-center px-4 py-2">
              <span className="text-base sm:text-lg font-medium">{monthName}</span>
            </div>
            <button
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}
              className="p-3 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors touch-manipulation"
              aria-label="Next month"
            >
              <ChevronRight className="w-5 h-5 sm:w-4 sm:h-4" />
            </button>
          </div>
        </div>

        {/* Legend - Mobile Responsive */}
        <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6 text-xs sm:text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-100 border border-green-200 rounded flex-shrink-0"></div>
            <span className="text-gray-600">Bookings</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-orange-100 border border-orange-200 rounded flex-shrink-0"></div>
            <span className="text-gray-600">Time Off</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-50 border border-blue-200 rounded flex-shrink-0"></div>
            <span className="text-gray-600">Today</span>
          </div>
        </div>

        {/* Calendar - Mobile Optimized */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          {/* Day Headers - Responsive */}
          <div className="grid grid-cols-7 gap-px bg-gray-200">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
              <div key={day} className="bg-gray-50 p-2 sm:p-3 text-center text-xs sm:text-sm font-medium text-gray-700">
                {/* Show abbreviated on mobile, full on desktop */}
                <span className="sm:hidden">{day.charAt(0)}</span>
                <span className="hidden sm:inline">{day}</span>
              </div>
            ))}
          </div>

          {/* Calendar Days - Responsive Grid */}
          <div className="grid grid-cols-7 gap-px bg-gray-200">
            {days.map((day, index) => {
              const dayBookings = day ? getBookingsForDate(day) : [];
              const dayTimeOffs = day ? getTimeOffsForDate(day) : [];

              // Filter out customer bookings that have associated admin student bookings
              const filteredBookings = dayBookings.filter(booking => {
                // Check if there's a student booking time-off for the same time slot
                const hasStudentBookingTimeOff = dayTimeOffs.some(timeOff =>
                  timeOff.isStudentBooking &&
                  timeOff.startTime === booking.startTime &&
                  timeOff.endTime === booking.endTime &&
                  (timeOff.serviceId._id || timeOff.serviceId) === booking.service._id
                );
                // Only include the booking if there's no associated student booking time-off
                return !hasStudentBookingTimeOff;
              });

              const allDayItems = [...filteredBookings.map(b => ({ ...b, type: 'booking' })), ...dayTimeOffs.map(t => ({ ...t, type: 'timeoff' }))];
              const isToday = day && day.toDateString() === new Date().toDateString();

              // Sort items to show future/active items first
              allDayItems.sort((a, b) => {
                const now = new Date();

                // Check if items have passed
                const aHasPassed = isToday && a.endTime ? (() => {
                  const [hours, minutes] = a.endTime.split(':').map(Number);
                  const aEndTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes);
                  return aEndTime < now;
                })() : false;

                const bHasPassed = isToday && b.endTime ? (() => {
                  const [hours, minutes] = b.endTime.split(':').map(Number);
                  const bEndTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes);
                  return bEndTime < now;
                })() : false;

                // Future/active items come first
                if (aHasPassed !== bHasPassed) {
                  return aHasPassed ? 1 : -1;
                }

                // Within same status, sort by start time (earliest first)
                if (a.startTime && b.startTime) {
                  return a.startTime.localeCompare(b.startTime);
                }

                return 0;
              });
              const isSelected = day && selectedDate && day.toDateString() === selectedDate.toDateString();

              // Check if this date/time has passed
              const now = new Date();
              const isPastDate = day && day < new Date(now.getFullYear(), now.getMonth(), now.getDate());

              return (
                <div
                  key={index}
                  className={`
                    min-h-[80px] sm:min-h-[100px] md:min-h-[120px] 
                    p-1 sm:p-2 bg-white relative
                    ${isToday ? 'bg-blue-50 ring-2 ring-blue-200' : ''}
                    ${isSelected ? 'ring-2 ring-blue-500' : ''}
                    ${isPastDate ? 'bg-gray-50 opacity-60 cursor-not-allowed' : ''}
                    ${day && !isPastDate ? 'cursor-pointer hover:bg-gray-50 active:bg-gray-100 touch-manipulation' : ''}
                    transition-colors
                  `}
                  onClick={() => {
                    if (day && !isPastDate) {
                      setDayItemsModalDate(day);
                      setShowDayItemsModal(true);
                    }
                  }}
                >
                  {day && (
                    <>
                      {/* Date Number - Responsive */}
                      <div className={`
                        text-xs sm:text-sm font-medium mb-1
                        ${isToday ? 'text-blue-600 font-bold' : isPastDate ? 'text-gray-400' : 'text-gray-900'}
                      `}>
                        {day.getDate()}
                        {/* Today indicator */}
                        {isToday && (
                          <div className="absolute top-1 right-1">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          </div>
                        )}
                      </div>

                      {/* Calendar Items - Mobile Optimized */}
                      <div className="space-y-0.5 sm:space-y-1">
                        {allDayItems.slice(0, isMobile ? 3 : 4).map((item, itemIndex) => {
                          // Check if this specific item has passed
                          let isItemPast = isPastDate;
                          if (isToday && item.endTime) {
                            const now = new Date();
                            const [hours, minutes] = item.endTime.split(':').map(Number);
                            const itemEndTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes);
                            isItemPast = itemEndTime < now;
                          }

                          return (
                            <div
                              key={itemIndex}
                              className={`
                                text-xs rounded truncate transition-all
                                px-1 py-0.5 sm:p-1 pointer-events-none
                                ${item.type === 'booking'
                                  ? isItemPast
                                    ? 'bg-green-50 text-green-600'
                                    : 'bg-green-200 text-green-900 font-medium border border-green-300'
                                  : item.isStudentBooking
                                    ? isItemPast
                                      ? 'bg-green-50 text-green-600'
                                      : 'bg-green-200 text-green-900 font-medium border border-green-300'
                                    : isItemPast
                                      ? 'bg-orange-50 text-orange-600'
                                      : 'bg-orange-200 text-orange-900 font-medium border border-orange-300'
                                }
                                ${isItemPast ? 'opacity-40' : ''}
                              `}
                              title={
                                item.type === 'booking'
                                  ? `${item.service?.name} - ${item.startTime} (${item.customerInfo?.firstName} ${item.customerInfo?.lastName})`
                                  : `Time Off - ${item.serviceId?.name || 'Unknown Service'} - ${item.isFullDay ? 'Full Day' : item.startTime + '-' + item.endTime}${item.reason ? ' - ' + item.reason : ''}`
                              }
                            >
                              {item.type === 'booking' ? (
                                <div className="flex items-center justify-between">
                                  <span className="truncate flex-1">
                                    {/* Mobile: Show just time, Desktop: Show service + time */}
                                    <span className="sm:hidden">{item.startTime}</span>
                                    <span className="hidden sm:inline">{item.service?.name} - {item.startTime}</span>
                                  </span>
                                </div>
                              ) : (
                                <div className="flex items-center justify-between">
                                  <span className="truncate flex-1">
                                    {/* Mobile: Shorter text */}
                                    <span className="sm:hidden">
                                      {item.isFullDay ? 'OFF' : item.startTime}
                                      {item.isStudentBooking && ` ${item.studentInfo?.firstName || '[Student]'}`}
                                    </span>
                                    <span className="hidden sm:inline">
                                      {item.isFullDay ? 'Time Off (Full Day)' : `Time Off ${item.startTime}`}
                                      {item.isStudentBooking && ` [${item.studentInfo?.firstName || 'Student'}]`}
                                    </span>
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {/* More items indicator */}
                        {allDayItems.length > (isMobile ? 3 : 4) && (
                          <div className="text-xs text-gray-500 text-center py-0.5">
                            +{allDayItems.length - (isMobile ? 3 : 4)} more
                          </div>
                        )}

                        {/* Empty state for clickable days */}
                        {allDayItems.length === 0 && (
                          <div className="hidden sm:flex items-center justify-center h-full opacity-0 hover:opacity-100 transition-opacity">
                            <Plus className="w-4 h-4 text-gray-400" />
                          </div>
                        )}
                      </div>

                      {/* Past date overlay */}
                      {isPastDate && (
                        <div className="absolute inset-0 bg-gray-100 bg-opacity-50 pointer-events-none"></div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>


      </div>
    );
  };

  const renderTimeOffTab = () => {
    return renderTimeOffCalendar();
  };

  const renderTimeOffForm = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold">
              {editingTimeOff ? "Edit Time Off" : "Add Time Off"}
            </h3>
            <div className="flex items-center space-x-2">

              <button
                onClick={resetTimeOffForm}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="space-y-6">
            {/* Service and Date Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Service</label>
                <select
                  value={timeOffForm.serviceId || (services.length > 0 ? services[0]._id : '')}
                  onChange={(e) =>
                    setTimeOffForm({ ...timeOffForm, serviceId: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-2"
                  required
                >
                  {services.map((service) => (
                    <option key={service._id} value={service._id}>
                      {service.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Date</label>
                <input
                  type="date"
                  value={timeOffForm.date}
                  onChange={(e) =>
                    setTimeOffForm({ ...timeOffForm, date: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-2"
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
            </div>

            {/* Full Day Option */}
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={timeOffForm.isFullDay}
                  onChange={(e) =>
                    setTimeOffForm({ ...timeOffForm, isFullDay: e.target.checked })
                  }
                  className="rounded"
                />
                <span className="text-sm font-medium">Full Day</span>
              </label>
            </div>

            {/* Reason for Full Day Time Off */}
            {timeOffForm.isFullDay && (
              <div>
                <label className="block text-sm font-medium mb-2">Reason (Optional)</label>
                <input
                  type="text"
                  value={timeOffForm.fullDayReason || ''}
                  onChange={(e) =>
                    setTimeOffForm({ ...timeOffForm, fullDayReason: e.target.value })
                  }
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Vacation, sick leave, maintenance, etc."
                />
              </div>
            )}

            {/* Time Slot Selection Options */}
            {!timeOffForm.isFullDay && (
              <div className="border-t pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-lg font-medium">Time Slots</h4>
                  <div className="flex items-center space-x-2">
                    {editingTimeOff && (
                      <span className="text-sm text-orange-600 bg-orange-100 px-2 py-1 rounded">
                        Note: Editing mode - changes will update existing time-off
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={addTimeOffSlot}
                      className="text-blue-500 hover:text-blue-700 text-sm flex items-center space-x-1"
                    >
                      <Plus className="w-4 h-4" />
                      <span>{editingTimeOff ? "Add Another Slot" : "Add Custom Slot"}</span>
                    </button>
                  </div>
                </div>

                {/* Predefined Slots Selection - Always Show */}
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  {timeOffForm.serviceId ? (
                    <>
                      <h5 className="text-sm font-medium mb-2">Quick Select - Predefined Slots:</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {getPredefinedTimeSlots().map((slot) => (
                          <button
                            key={slot.id}
                            type="button"
                            onClick={() => selectPredefinedSlot(slot)}
                            className="text-left p-2 border rounded hover:bg-blue-50 hover:border-blue-300 text-sm transition-colors"
                          >
                            {slot.label}
                          </button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="text-center text-gray-500">
                      <p className="text-sm">Please select a service first to see predefined slots</p>
                    </div>
                  )}
                </div>

                {/* Time Slots */}
                <div className="space-y-4">
                  {timeOffForm.timeSlots.map((slot, index) => (
                    <div key={slot.id} className="border rounded-lg p-4 bg-gray-50">
                      <div className="flex justify-between items-center mb-4">
                        <div className="flex items-center space-x-2">
                          <h5 className="font-medium">Slot {index + 1}</h5>
                          {slot.isStudentBooking && (slot.studentInfo.firstName || slot.studentInfo.lastName || slot.studentInfo.email) && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Will create booking
                            </span>
                          )}
                          {slot.isStudentBooking && !(slot.studentInfo.firstName || slot.studentInfo.lastName || slot.studentInfo.email) && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              Time-off only
                            </span>
                          )}
                        </div>
                        {timeOffForm.timeSlots.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeTimeOffSlot(slot.id)}
                            className="flex items-center justify-center min-w-[44px] min-h-[44px] md:min-w-[32px] md:min-h-[32px] text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors p-2 md:p-1"
                            title="Delete time slot"
                            aria-label="Delete time slot"
                          >
                            <Trash2 className="w-5 h-5 md:w-4 md:h-4" />
                            <span className="ml-1 text-xs sm:hidden">Delete</span>
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Start Time</label>
                          <input
                            type="time"
                            value={slot.startTime}
                            onChange={(e) =>
                              updateTimeOffSlot(slot.id, 'startTime', e.target.value)
                            }
                            className="w-full border rounded-lg px-3 py-2"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">End Time</label>
                          <input
                            type="time"
                            value={slot.endTime}
                            onChange={(e) =>
                              updateTimeOffSlot(slot.id, 'endTime', e.target.value)
                            }
                            className="w-full border rounded-lg px-3 py-2"
                            required
                          />
                        </div>
                      </div>

                      <div className="mb-4">
                        <label className="block text-sm font-medium mb-2">Reason (Optional)</label>
                        <input
                          type="text"
                          value={slot.reason}
                          onChange={(e) =>
                            updateTimeOffSlot(slot.id, 'reason', e.target.value)
                          }
                          className="w-full border rounded-lg px-3 py-2"
                          placeholder="Vacation, sick leave, maintenance, etc."
                        />
                      </div>

                      {/* Student Booking Option */}
                      <div className="mb-4">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={slot.isStudentBooking}
                            onChange={(e) =>
                              updateTimeOffSlot(slot.id, 'isStudentBooking', e.target.checked)
                            }
                            className="rounded"
                          />
                          <span className="text-sm font-medium">This is for a student booking</span>
                        </label>

                      </div>

                      {/* Student Information */}
                      {slot.isStudentBooking && (
                        <div className="border-t pt-4 space-y-4">
                          <h6 className="font-medium text-sm">Student Information</h6>

                          {/* Basic Customer Info */}
                          <div className="space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-medium mb-1">First Name <span className="text-red-500">*</span></label>
                                <input
                                  type="text"
                                  value={slot.studentInfo.firstName}
                                  onChange={(e) =>
                                    updateStudentInfo(slot.id, 'firstName', e.target.value)
                                  }
                                  className="w-full border rounded px-2 py-1 text-sm"
                                  placeholder="Student's first name"
                                  required
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium mb-1">Last Name</label>
                                <input
                                  type="text"
                                  value={slot.studentInfo.lastName}
                                  onChange={(e) =>
                                    updateStudentInfo(slot.id, 'lastName', e.target.value)
                                  }
                                  className="w-full border rounded px-2 py-1 text-sm"
                                  placeholder="Student's last name (optional)"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium mb-1">Email <span className="text-red-500">*</span></label>
                                <input
                                  type="email"
                                  value={slot.studentInfo.email}
                                  onChange={(e) =>
                                    updateStudentInfo(slot.id, 'email', e.target.value)
                                  }
                                  className="w-full border rounded px-2 py-1 text-sm"
                                  placeholder="student@example.com"
                                  required
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium mb-1">Phone <span className="text-red-500">*</span></label>
                                <input
                                  type="tel"
                                  value={slot.studentInfo.phone}
                                  onChange={(e) =>
                                    updateStudentInfo(slot.id, 'phone', e.target.value)
                                  }
                                  className="w-full border rounded px-2 py-1 text-sm"
                                  placeholder="(555) 123-4567"
                                  required
                                />
                              </div>
                              {/* Show company field for driving_class services */}
                              {(() => {
                                const selectedService = services.find(s => s._id === timeOffForm.serviceId);
                                return selectedService?.category === 'driving_class' && (
                                  <div className="md:col-span-2">
                                    <label className="block text-xs font-medium mb-1">Company</label>
                                    <input
                                      type="text"
                                      value={slot.studentInfo.company}
                                      onChange={(e) =>
                                        updateStudentInfo(slot.id, 'company', e.target.value)
                                      }
                                      className="w-full border rounded px-2 py-1 text-sm"
                                      placeholder="Company name (if applicable)"
                                    />
                                  </div>
                                );
                              })()}
                              {/* ICBC Test Location */}
                              <div className="md:col-span-2">
                                <label className="block text-xs font-medium mb-1">At which ICBC Location Do You Want to Pick Up and Drop Off?</label>
                                <select
                                  value={slot.studentInfo.icbcLocation || ''}
                                  onChange={(e) =>
                                    updateStudentInfo(slot.id, 'icbcLocation', e.target.value)
                                  }
                                  className="w-full border rounded px-2 py-1 text-sm"
                                >
                                  <option value="">Select an ICBC location</option>
                                  {icbcLocations.map((location) => (
                                    <option key={location.key} value={location.key}>
                                      {location.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </div>

                          {/* Custom Form Fields */}
                          {(() => {
                            const selectedService = services.find(s => s._id === timeOffForm.serviceId);
                            return selectedService?.formFields && selectedService.formFields.length > 0 && (
                              <div className="space-y-3">
                                <h6 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Additional Information</h6>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {selectedService.formFields.map((field) => (
                                    <div
                                      key={field.fieldId}
                                      className={`${field.type === "textarea" ? "md:col-span-2" : ""}`}
                                    >
                                      <label className="block text-xs font-medium mb-1">
                                        {field.label}
                                        {field.required && (
                                          <span className="text-red-500 ml-1">*</span>
                                        )}
                                      </label>
                                      {renderStudentFormField(
                                        field,
                                        slot.id,
                                        slot.studentInfo.formResponses[field.fieldId]
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          })()}

                          {/* Notes */}
                          <div>
                            <label className="block text-xs font-medium mb-1">Notes</label>
                            <textarea
                              value={slot.studentInfo.notes}
                              onChange={(e) =>
                                updateStudentInfo(slot.id, 'notes', e.target.value)
                              }
                              className="w-full border rounded px-2 py-1 text-sm"
                              rows="2"
                              placeholder="Additional notes about the student or booking..."
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-6 border-t">
              <div>
                {editingTimeOff && (
                  <button
                    onClick={async () => {
                      const isStudentBooking = editingTimeOff?.isStudentBooking && editingTimeOff?.associatedBooking;
                      let confirmMessage = "Are you sure you want to delete this time-off period?";
                      if (isStudentBooking) {
                        confirmMessage = "Are you sure you want to delete this time-off period? This will also delete the associated student booking.";
                      }

                      if (window.confirm(confirmMessage)) {
                        await handleDeleteTimeOff(editingTimeOff._id);
                        resetTimeOffForm();
                      }
                    }}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center space-x-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete</span>
                  </button>
                )}
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={resetTimeOffForm}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateTimeOff}
                  disabled={!timeOffForm.serviceId || !timeOffForm.date || (!timeOffForm.isFullDay && timeOffForm.timeSlots.length === 0)}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  <Save className="w-4 h-4" />
                  <span>
                    {editingTimeOff ? "Update" : (() => {
                      const studentBookings = timeOffForm.timeSlots.filter(slot =>
                        slot.isStudentBooking && (slot.studentInfo.firstName || slot.studentInfo.lastName || slot.studentInfo.email)
                      ).length;

                      if (studentBookings > 0) {
                        return `Add Time Off + ${studentBookings} Booking(s)`;
                      }
                      return "Add Time Off";
                    })()}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderDayItemsModal = () => {
    if (!showDayItemsModal || !dayItemsModalDate) return null;

    const dayBookings = getBookingsForDate(dayItemsModalDate);
    const dayTimeOffs = getTimeOffsForDate(dayItemsModalDate);

    // Filter out customer bookings that have associated admin student bookings
    const filteredBookings = dayBookings.filter(booking => {
      const hasStudentBookingTimeOff = dayTimeOffs.some(timeOff =>
        timeOff.isStudentBooking &&
        timeOff.startTime === booking.startTime &&
        timeOff.endTime === booking.endTime &&
        (timeOff.serviceId._id || timeOff.serviceId) === booking.service._id
      );
      return !hasStudentBookingTimeOff;
    });

    const studentBookingTimeOffs = dayTimeOffs.filter(timeOff => timeOff.isStudentBooking);
    const regularTimeOffs = dayTimeOffs.filter(timeOff => !timeOff.isStudentBooking);

    const allBookings = [...filteredBookings, ...studentBookingTimeOffs];
    const hasAnyItems = allBookings.length > 0 || regularTimeOffs.length > 0;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-4 md:p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                Schedule for {dayItemsModalDate.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  timeZone: 'America/Vancouver'
                })}
              </h3>
              <button
                onClick={() => {
                  setShowDayItemsModal(false);
                  setDayItemsModalDate(null);
                }}
                className="text-gray-500 hover:text-gray-700 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {!hasAnyItems ? (
              <div className="text-center py-8">
                <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500 mb-4">No bookings or time-offs for this date</p>
                {activeTab === "timeoff" && (
                  <button
                    onClick={() => {
                      setTimeOffForm({
                        serviceId: services.length > 0 ? services[0]._id : '',
                        date: dayItemsModalDate.toISOString().split('T')[0],
                        isFullDay: true,
                        usePredefinedSlots: false,
                        fullDayReason: '',
                        timeSlots: []
                      });
                      setEditingTimeOff(null);
                      setShowTimeOffForm(true);
                      setShowDayItemsModal(false);
                    }}
                    className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center space-x-2 mx-auto"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Time Off</span>
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {/* Bookings Section */}
                {allBookings.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-green-700 mb-3 flex items-center">
                      <div className="w-3 h-3 bg-green-100 border border-green-200 rounded mr-2 flex-shrink-0"></div>
                      Bookings ({allBookings.length})
                    </h4>
                    <div className="space-y-2">
                      {allBookings.map((item) => (
                        <div
                          key={item._id}
                          className="border border-green-200 bg-green-50 rounded-lg p-3 hover:bg-green-100 active:bg-green-200 cursor-pointer transition-colors"
                          onClick={() => {
                            console.log('Booking clicked:', item);
                            console.log('Has customerInfo:', !!item.customerInfo);
                            console.log('Has associatedBooking:', !!item.associatedBooking);

                            if (item.customerInfo) {
                              // Regular booking - show booking details
                              console.log('Opening booking details for regular booking');
                              setSelectedBooking(item);
                              setShowBookingDetails(true);
                              setShowDayItemsModal(false);
                            } else if (item.associatedBooking) {
                              // Student booking time-off with associated booking - find booking in bookings array
                              console.log('Looking for associated booking:', item.associatedBooking);
                              const associatedBooking = bookings.find(b => b._id === item.associatedBooking);
                              console.log('Found associated booking:', associatedBooking);

                              if (associatedBooking) {
                                setSelectedBooking(associatedBooking);
                                setShowBookingDetails(true);
                                setShowDayItemsModal(false);
                              } else {
                                console.log('Associated booking not found, opening time-off edit');
                                // Fallback to time-off edit if booking not found
                                handleEditTimeOff(item);
                                setShowDayItemsModal(false);
                              }
                            } else {
                              console.log('No customerInfo or associatedBooking, opening time-off edit');
                              // Student booking time-off without associated booking - show time-off edit form
                              handleEditTimeOff(item);
                              setShowDayItemsModal(false);
                            }
                          }}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2 mb-1">
                                <h5 className="font-medium text-green-900 truncate">
                                  {item.service?.name || item.serviceId?.name || 'Unknown Service'}
                                </h5>
                                {item.isStudentBooking && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    Admin
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-green-700 mb-1">
                                {item.customerInfo ? (
                                  `${item.customerInfo.firstName} ${item.customerInfo.lastName}`
                                ) : item.studentInfo ? (
                                  `${item.studentInfo.firstName || ''} ${item.studentInfo.lastName || ''}`.trim() || 'Student'
                                ) : (
                                  'Student'
                                )}
                              </p>
                              {(item.studentInfo?.icbcLocation || item.customerInfo?.location) && (
                                <p className="text-sm text-green-700 font-medium mb-1">
                                  {formatLocationName(item.studentInfo?.icbcLocation || item.customerInfo?.location)}
                                </p>
                              )}
                              <p className="text-sm text-green-600">
                                {item.startTime} - {item.endTime} {item.duration ? `(${item.duration} min)` : ''}
                              </p>
                              {item.reason && (
                                <p className="text-sm text-green-600 mt-1">
                                  {item.reason}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              {item.status && (
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                                  {item.status}
                                </span>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (item.customerInfo) {
                                    // Regular booking - delete booking
                                    handleDeleteBooking(item._id);
                                  } else {
                                    // Student booking time-off - delete time-off
                                    handleDeleteTimeOff(item._id);
                                  }
                                  setShowDayItemsModal(false);
                                }}
                                className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded transition-colors"
                                title="Delete booking"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                              <div className="text-blue-600">
                                <Edit className="w-4 h-4" />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Time-offs Section */}
                {regularTimeOffs.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-orange-700 mb-3 flex items-center">
                      <div className="w-3 h-3 bg-orange-100 border border-orange-200 rounded mr-2 flex-shrink-0"></div>
                      Time Off ({regularTimeOffs.length})
                    </h4>
                    <div className="space-y-2">
                      {regularTimeOffs.map((timeOff) => (
                        <div
                          key={timeOff._id}
                          className="border border-orange-200 bg-orange-50 rounded-lg p-3 hover:bg-orange-100 active:bg-orange-200 cursor-pointer transition-colors"
                          onClick={() => {
                            handleEditTimeOff(timeOff);
                            setShowDayItemsModal(false);
                          }}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1 min-w-0">
                              <h5 className="font-medium text-orange-900 mb-1 truncate">
                                {timeOff.serviceId?.name || 'Unknown Service'}
                              </h5>
                              <p className="text-sm text-orange-700 mb-1">
                                {timeOff.isFullDay
                                  ? 'Full Day'
                                  : `${timeOff.startTime} - ${timeOff.endTime}`
                                }
                              </p>
                              {timeOff.reason && (
                                <p className="text-sm text-orange-600">
                                  {timeOff.reason}
                                </p>
                              )}
                              {timeOff.studentInfo?.icbcLocation && (
                                <p className="text-sm text-orange-600 mt-1">
                                  {formatLocationName(timeOff.studentInfo.icbcLocation)}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteTimeOff(timeOff._id);
                                  setShowDayItemsModal(false);
                                }}
                                className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded transition-colors"
                                title="Delete time-off"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                              <div className="text-blue-600">
                                <Edit className="w-4 h-4" />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add Time Off Button - only show on Time Off tab */}
                {activeTab === "timeoff" && (
                  <div className="pt-4 border-t">
                    <button
                      onClick={() => {
                        setTimeOffForm({
                          serviceId: services.length > 0 ? services[0]._id : '',
                          date: dayItemsModalDate.toISOString().split('T')[0],
                          isFullDay: true,
                          usePredefinedSlots: false,
                          fullDayReason: '',
                          timeSlots: []
                        });
                        setEditingTimeOff(null);
                        setShowTimeOffForm(true);
                        setShowDayItemsModal(false);
                      }}
                      className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center space-x-2"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add Time Off</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderRescheduleModal = () => {
    if (!rescheduleBooking) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-md w-full">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Reschedule Booking</h3>
              <button
                onClick={() => setShowRescheduleModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Current Booking</label>
                <div className="bg-gray-50 p-3 rounded">
                  <p className="font-medium">{rescheduleBooking.service?.name}</p>
                  <p className="text-sm text-gray-600">
                    {new Date(rescheduleBooking.bookingDate).toLocaleDateString('en-US', {
                      timeZone: 'America/Vancouver'
                    })} at {rescheduleBooking.startTime}
                  </p>
                  <p className="text-sm text-gray-500">
                    {rescheduleBooking.customerInfo.firstName} {rescheduleBooking.customerInfo.lastName}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">New Date</label>
                <input
                  type="date"
                  value={rescheduleDate}
                  onChange={(e) => {
                    setRescheduleDate(e.target.value);
                    if (e.target.value && rescheduleBooking) {
                      fetchAvailableTimeSlots(e.target.value, rescheduleBooking._id);
                    }
                  }}
                  className="w-full border rounded-lg px-3 py-2"
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">New Time</label>
                {loadingTimeSlots ? (
                  <p className="text-sm text-gray-500">Loading available times...</p>
                ) : availableTimeSlots.length > 0 ? (
                  <select
                    value={rescheduleTime}
                    onChange={(e) => setRescheduleTime(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="">Select a time</option>
                    {availableTimeSlots.map((slot, index) => (
                      <option key={index} value={slot.startTime}>
                        {slot.startTime} - {slot.endTime}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="text-sm text-red-500">No available times for selected date</p>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => setShowRescheduleModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRescheduleSubmit}
                  disabled={!rescheduleDate || !rescheduleTime}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Reschedule
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold">Booking Management</h2>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("services")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === "services"
              ? "border-blue-500 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
          >
            Services
          </button>
          <button
            onClick={() => setActiveTab("bookings")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === "bookings"
              ? "border-blue-500 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
          >
            Bookings
          </button>
          <button
            onClick={() => setActiveTab("calendar")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === "calendar"
              ? "border-blue-500 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
          >
            <CalendarIcon className="inline w-4 h-4 mr-1" />
            Calendar
          </button>
          <button
            onClick={() => setActiveTab("timeoff")}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === "timeoff"
              ? "border-blue-500 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
          >
            <Clock className="inline w-4 h-4 mr-1" />
            Time Off
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "services" && renderServicesTab()}
      {activeTab === "bookings" && renderBookingsTab()}
      {activeTab === "calendar" && renderCalendarTab()}
      {activeTab === "timeoff" && renderTimeOffTab()}

      {/* Service Form Modal */}
      {showServiceForm && renderServiceForm()}

      {/* Time Off Form Modal */}
      {showTimeOffForm && renderTimeOffForm()}

      {/* Day Items Modal */}
      {showDayItemsModal && renderDayItemsModal()}

      {/* Booking Details Modal */}
      {showBookingDetails && renderBookingDetails()}

      {/* Edit Booking Modal */}
      {showEditBookingForm && renderEditBookingModal()}

      {/* Reschedule Modal */}
      {showRescheduleModal && renderRescheduleModal()}
    </div>
  );
};

export default BookingManagement;
